import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { COLORS, ROUTINE_DATA, CYCLE_COLORS, API, getDayStyle } from '../utils/constants';
import { fmtFecha } from '../utils/helpers';
import { descargaActiva, bloqueoActivo, pesoDescargaEj, baselineDe, exentoBloqueo, exentoBloqueoBaseline } from '../utils/recovery';
import SwipeableRow from '../components/SwipeableRow';
import Popup from '../components/Popup';
import DaySelector from '../components/DaySelector';
import { IoReorderTwoOutline } from 'react-icons/io5';

// Migración única: descanso por defecto a 120s (2 min) para todos los ejercicios ya guardados.
// Resetea los overrides de descanso en localStorage (fitapp_rest_<name> y fitapp_ex_<name>.rest)
// para que el nuevo valor por defecto no quede tapado por ajustes previos por ejercicio.
(function migrateRestDefault120() {
  try {
    if (localStorage.getItem('fitapp_migr_rest120')) return;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('fitapp_rest_')) {
        localStorage.setItem(k, '120');
      } else if (k.startsWith('fitapp_ex_')) {
        try {
          const obj = JSON.parse(localStorage.getItem(k) || '{}');
          if (obj && typeof obj === 'object' && 'rest' in obj) {
            obj.rest = 120;
            localStorage.setItem(k, JSON.stringify(obj));
          }
        } catch {}
      }
    }
    localStorage.setItem('fitapp_migr_rest120', '1');
  } catch {}
})();

// Contador de IDs estables para ejercicios y series (no depende de índice)
let _idCounter = 0;

function loadExLS(name) {
  try { return JSON.parse(localStorage.getItem(`fitapp_ex_${name}`)) || {}; } catch { return {}; }
}
function saveExLS(name, patch) {
  try {
    const cur = loadExLS(name);
    localStorage.setItem(`fitapp_ex_${name}`, JSON.stringify({ ...cur, ...patch }));
  } catch {}
}

async function resizeImage(file, maxPx = 1200, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

function initExercises(todayType, activeRoutine) {
  const routineData = (activeRoutine?.dias?.[todayType]) ?? (ROUTINE_DATA[todayType]) ?? [];
  return routineData.map(ex => {
    const saved = loadExLS(ex.name);
    return {
      ...ex,
      id: `ex_${++_idCounter}`,
      machine: saved.machine !== undefined ? saved.machine : (ex.machine || ''),
      position: saved.position !== undefined ? saved.position : (ex.position || ''),
      reps: saved.reps !== undefined ? saved.reps : (ex.reps !== undefined ? String(ex.reps) : ''),
      weight: saved.weight !== undefined ? saved.weight : (ex.weight !== undefined ? String(ex.weight) : ''),
      rest: saved.rest !== undefined ? saved.rest : (ex.rest || 120),
      notes: saved.notes || '',
      sets: Array.from({ length: ex.sets }, () => ({
        id: `set_${++_idCounter}`,
        done: false,
      })),
    };
  });
}

function getAllKnownExerciseNames(activeRoutine) {
  const names = new Set();
  Object.values(ROUTINE_DATA).forEach(exArr => {
    if (Array.isArray(exArr)) exArr.forEach(ex => names.add(ex.name));
  });
  if (activeRoutine?.dias) {
    Object.values(activeRoutine.dias).forEach(exArr => {
      if (Array.isArray(exArr)) exArr.forEach(ex => names.add(ex.name));
    });
  }
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('fitapp_ex_')) names.add(key.replace('fitapp_ex_', ''));
    }
  } catch {}
  return [...names].sort();
}

function ProgresoView({ onClose, API, todayType }) {
  const COLORES = {
    Push: '#ff6b35', Legs: '#2ecc71', Pull: '#4fc3f7', Descanso: '#9b59b6',
  };
  const PERIODOS = [
    { label: '30d', days: 30 },
    { label: '3m', days: 90 },
    { label: '6m', days: 180 },
    { label: 'Todo', days: 0 },
  ];
  const METRICAS_SESION = [
    { key: 'duracion_secs', label: 'Duración (min)', fmt: v => v ? (v/60).toFixed(1) : 0, unit: 'min' },
    { key: 'volumen_total', label: 'Volumen total (kg)', fmt: v => v || 0, unit: 'kg' },
    { key: 'total_reps', label: 'Repeticiones totales', fmt: v => v || 0, unit: 'reps' },
    { key: 'n_ejercicios', label: 'Ejercicios', fmt: v => v || 0, unit: '' },
  ];
  const METRICAS_EJERCICIO = [
    { key: 'peso_max', label: 'Peso máximo', unit: 'kg' },
    { key: 'peso_avg', label: 'Peso promedio', unit: 'kg' },
    { key: 'reps_total', label: 'Reps totales por sesión', unit: 'reps' },
    { key: 'reps_avg', label: 'Reps promedio por serie', unit: 'reps' },
    { key: 'volumen', label: 'Volumen (kg×reps)', unit: 'kg' },
    { key: 'total_secs', label: 'Tiempo ejercicio', unit: 's', fmt: v => v ? (v/60).toFixed(1) : null, fmtUnit: 'min' },
    { key: 'n_series', label: 'Nº series', unit: '' },
  ];

  const [modo, setModo] = React.useState('sesion'); // 'sesion' | 'ejercicio'
  const [periodoIdx, setPeriodoIdx] = React.useState(0);
  const [metricaSesion, setMetricaSesion] = React.useState('volumen_total');
  const [ejercicios, setEjercicios] = React.useState([]);
  const [ejercicioSel, setEjercicioSel] = React.useState('');
  const [metricaEj, setMetricaEj] = React.useState('peso_max');
  const [datos, setDatos] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [listaLimit, setListaLimit] = React.useState(20);
  const [tipoSel, setTipoSel] = React.useState(() => (todayType && todayType !== 'Descanso') ? todayType : 'Pull');
  const [expandedSession, setExpandedSession] = React.useState(null);
  const chartRef = React.useRef(null);
  const chartInstanceRef = React.useRef(null);

  const getDesdeFecha = React.useCallback(() => {
    const days = PERIODOS[periodoIdx].days;
    if (!days) return null;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }, [periodoIdx]);

  // Cargar lista de ejercicios al montar (filtrada por tipo_dia actual)
  React.useEffect(() => {
    const qs = (todayType && todayType !== 'Descanso') ? '?tipo_dia=' + encodeURIComponent(todayType) : '';
    fetch(API + '/entrenamiento/progreso/ejercicios-lista' + qs, { credentials: 'include' })
      .then(r => r.json())
      .then(lista => { setEjercicios(lista); if (lista.length) setEjercicioSel(lista[0]); })
      .catch(() => {});
  }, [API, todayType]);

  // Reset lista limit when period/mode/exercise/tipo changes
  React.useEffect(() => { setListaLimit(20); setExpandedSession(null); }, [modo, periodoIdx, ejercicioSel, tipoSel]);

  // Cargar datos según modo
  React.useEffect(() => {
    setLoading(true);
    const desde = getDesdeFecha();
    const params = new URLSearchParams();
    if (desde) params.set('desde', desde);

    if (modo === 'tipo') params.set('tipo', tipoSel);
    const url = modo === 'ejercicio'
      ? (ejercicioSel ? `${API}/entrenamiento/progreso/ejercicio?nombre=${encodeURIComponent(ejercicioSel)}&${params}` : null)
      : `${API}/entrenamiento/progreso/sesiones?${params}`;

    if (!url) { setLoading(false); return; }

    fetch(url, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setDatos(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [modo, periodoIdx, ejercicioSel, tipoSel, API, getDesdeFecha]);

  // Renderizar chart cuando cambian datos o métrica
  React.useEffect(() => {
    if (!chartRef.current || !datos.length) return;
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const metricaKey = modo === 'ejercicio' ? metricaEj : metricaSesion;
    const metricaDef = modo === 'ejercicio'
      ? METRICAS_EJERCICIO.find(m => m.key === metricaKey)
      : METRICAS_SESION.find(m => m.key === metricaKey);

    // Build chart entries with period boundary anchors so x-axis always covers the full selected period
    const hoy = new Date().toISOString().slice(0, 10);
    const desdeDate = getDesdeFecha();
    const allEntries = [];
    if (desdeDate && datos.length > 0 && datos[0].fecha > desdeDate) {
      allEntries.push({ fecha: desdeDate, _boundary: true });
    }
    allEntries.push(...datos);
    if (datos.length > 0 && datos[datos.length - 1].fecha < hoy) {
      allEntries.push({ fecha: hoy, _boundary: true });
    }

    const labels = allEntries.map(d => {
      const [, mes, dia] = d.fecha.split('-');
      return `${dia}/${mes}`;
    });

    const rawValues = allEntries.map(d => d._boundary ? null : d[metricaKey]);
    const values = metricaDef?.fmt
      ? rawValues.map(v => v != null ? parseFloat(metricaDef.fmt(v)) : null)
      : rawValues.map(v => v != null ? v : null);

    const colorByTipo = allEntries.map(d => d._boundary ? 'transparent' : (COLORES[d.tipo_dia] || '#fd8b00'));
    const pointRadii = allEntries.map(d => d._boundary ? 0 : 5);
    const accentColor = COLORES[todayType] || '#fd8b00';

    const nonNullVals = values.filter(v => v != null);
    const dataMin = nonNullVals.length ? Math.min(...nonNullVals) : 0;
    const dataMax = nonNullVals.length ? Math.max(...nonNullVals) : 0;
    const yRange = dataMax - dataMin;
    const yPad = yRange > 0 ? yRange * 0.1 : Math.max(dataMin * 0.05, 5);
    const yMin = Math.max(0, dataMin - yPad);
    const yMax = dataMax + yPad;

    import('chart.js').then(({ Chart, registerables }) => {
      Chart.register(...registerables);
      chartInstanceRef.current = new Chart(chartRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: metricaDef?.label || metricaKey,
            data: values,
            borderColor: accentColor,
            backgroundColor: `${accentColor}20`,
            pointBackgroundColor: colorByTipo,
            pointBorderColor: colorByTipo,
            pointRadius: pointRadii,
            pointHoverRadius: allEntries.map(d => d._boundary ? 0 : 7),
            borderWidth: 2,
            tension: 0.3,
            fill: true,
            spanGaps: true,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              filter: item => !allEntries[item.dataIndex]?._boundary && item.parsed.y != null,
              callbacks: {
                label: ctx => {
                  const d = allEntries[ctx.dataIndex];
                  if (!d || d._boundary) return '';
                  const unit = metricaDef?.fmtUnit || metricaDef?.unit || '';
                  return `${ctx.parsed.y} ${unit}${d.tipo_dia ? ' · ' + d.tipo_dia : ''}`;
                },
              },
              backgroundColor: '#1c1b1b',
              titleColor: '#efffe3',
              bodyColor: '#baccb0',
              borderColor: 'rgba(255,255,255,0.1)',
              borderWidth: 1,
            },
          },
          scales: {
            x: {
              ticks: { color: '#85967c', font: { size: 10 }, maxTicksLimit: 8 },
              grid: { color: 'rgba(255,255,255,0.04)' },
            },
            y: {
              min: yMin,
              max: yMax,
              ticks: { color: '#baccb0', font: { size: 11 } },
              grid: { color: 'rgba(255,255,255,0.06)' },
            },
          },
        },
      });
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [datos, metricaSesion, metricaEj, modo]);

  const metricaActual = modo === 'ejercicio'
    ? METRICAS_EJERCICIO.find(m => m.key === metricaEj)
    : METRICAS_SESION.find(m => m.key === metricaSesion);

  const valores = datos.map(d => {
    const v = d[modo === 'ejercicio' ? metricaEj : metricaSesion];
    return metricaActual?.fmt ? parseFloat(metricaActual.fmt(v)) : v;
  }).filter(v => v != null);

  const valMax = valores.length ? Math.max(...valores) : 0;
  const valMin = valores.length ? Math.min(...valores) : 0;
  const valUlt = valores.length ? valores[valores.length - 1] : null;
  const acento = modo === 'tipo' ? (COLORES[tipoSel] || '#fd8b00')
    : modo === 'ejercicio' ? (COLORES[todayType] || '#fd8b00')
    : '#fd8b00';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'linear-gradient(135deg, #2D1A4A 0%, #151624 50%, #0F2D4A 100%)',
      overflowY: 'auto', padding: '0 0 100px',
    }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(21,22,36,0.95)', backdropFilter: 'blur(12px)', padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '6px 12px', color: '#baccb0', fontSize: 13, cursor: 'pointer' }}>←</button>
          <h2 style={{ margin: 0, color: '#efffe3', fontSize: 20, fontWeight: 900, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>📈 Progreso</h2>
          {/* Tipo de sesión actual */}
          {todayType && todayType !== 'Descanso' && (
            <div style={{ marginLeft: 'auto' }}>
              <span style={{ fontSize: 12, color: COLORES[todayType] || '#fd8b00', fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: (COLORES[todayType] || '#fd8b00') + '22' }}>
                ● {todayType}
              </span>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Controles: modo + periodo */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {/* Modo */}
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 3, gap: 3 }}>
            {[['sesion','General'],['tipo','Por tipo'],['ejercicio','Por ejercicio']].map(([m, label]) => (
              <button key={m} onClick={() => setModo(m)} style={{
                background: modo === m ? (m === 'tipo' ? COLORES[tipoSel] || acento : acento) : 'transparent',
                color: modo === m ? '#fff' : '#baccb0',
                border: 'none', borderRadius: 7, padding: '5px 12px',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}>{label}</button>
            ))}
          </div>
          {/* Periodo */}
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 3, gap: 3, marginLeft: 'auto' }}>
            {PERIODOS.map((p, i) => (
              <button key={p.label} onClick={() => setPeriodoIdx(i)} style={{
                background: periodoIdx === i ? acento + '4d' : 'transparent',
                color: periodoIdx === i ? acento : '#baccb0',
                border: periodoIdx === i ? '1px solid ' + acento + '66' : '1px solid transparent',
                borderRadius: 7, padding: '5px 10px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>{p.label}</button>
            ))}
          </div>
        </div>

        {/* Selector tipo (solo en modo tipo) */}
        {modo === 'tipo' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {['Pull', 'Legs', 'Push'].map(t => (
              <button key={t} onClick={() => setTipoSel(t)} style={{
                flex: 1, padding: '8px 0', borderRadius: 10, fontWeight: 800, fontSize: 13, cursor: 'pointer', border: 'none',
                background: tipoSel === t ? COLORES[t] : 'rgba(255,255,255,0.05)',
                color: tipoSel === t ? '#fff' : COLORES[t] || '#baccb0',
              }}>{t}</button>
            ))}
          </div>
        )}

        {/* Selector ejercicio (solo en modo ejercicio) */}
        {modo === 'ejercicio' && (
          <div style={{ marginBottom: 14 }}>
            <select
              value={ejercicioSel}
              onChange={e => setEjercicioSel(e.target.value)}
              style={{
                width: '100%', background: '#1c1b1b', color: '#efffe3',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
                padding: '10px 14px', fontSize: 14, fontWeight: 600,
              }}
            >
              {ejercicios.map(ej => <option key={ej} value={ej}>{ej}</option>)}
            </select>
          </div>
        )}

        {/* Selector métrica */}
        <div style={{ marginBottom: 14, overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 6, paddingBottom: 4 }}>
            {(modo === 'ejercicio' ? METRICAS_EJERCICIO : METRICAS_SESION).map(m => (
              <button
                key={m.key}
                onClick={() => modo === 'ejercicio' ? setMetricaEj(m.key) : setMetricaSesion(m.key)}
                style={{
                  background: (modo === 'ejercicio' ? metricaEj : metricaSesion) === m.key ? acento + '33' : 'rgba(255,255,255,0.05)',
                  color: (modo === 'ejercicio' ? metricaEj : metricaSesion) === m.key ? acento : '#85967c',
                  border: (modo === 'ejercicio' ? metricaEj : metricaSesion) === m.key ? '1px solid ' + acento + '66' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '6px 12px', whiteSpace: 'nowrap',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                }}
              >{m.label}</button>
            ))}
          </div>
        </div>

        {/* Stats resumen */}
        {!loading && datos.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
            {[
              { label: 'Máximo', value: valMax },
              { label: 'Mínimo', value: valMin },
              { label: 'Último', value: valUlt },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ color: '#85967c', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{s.label}</div>
                <div style={{ color: '#efffe3', fontSize: 18, fontWeight: 900, fontFamily: 'monospace' }}>
                  {s.value != null ? s.value : '—'} <span style={{ fontSize: 10, color: '#baccb0' }}>{metricaActual?.fmtUnit || metricaActual?.unit}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chart */}
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: '16px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: 16, position: 'relative', height: 320 }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#baccb0', fontSize: 13 }}>Cargando...</div>
          )}
          {!loading && datos.length === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 32 }}>📊</div>
              <div style={{ color: '#85967c', fontSize: 13 }}>Sin datos para este período</div>
            </div>
          )}
          <canvas ref={chartRef} style={{ display: loading || datos.length === 0 ? 'none' : 'block' }} />
        </div>

        {/* Lista de sesiones (resumen) */}
        {!loading && datos.length > 0 && (
          <div>
            <div style={{ color: '#85967c', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              {datos.length} {datos.length === 1 ? 'sesión' : 'sesiones'}{modo === 'tipo' ? ' · ' + tipoSel : ''}
            </div>
            {[...datos].reverse().slice(0, listaLimit).map((d, i) => {
              const v = d[modo === 'sesion' || modo === 'tipo' ? metricaSesion : metricaEj];
              const vFmt = metricaActual?.fmt ? metricaActual.fmt(v) : v;
              const color = COLORES[d.tipo_dia] || '#fd8b00';
              const isExpanded = expandedSession === i;
              return (
                <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  {/* Fila resumen (siempre visible) */}
                  <div
                    onClick={() => setExpandedSession(isExpanded ? null : i)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }}
                  >
                    <span style={{ color, fontSize: 10, fontWeight: 700, minWidth: 36 }}>{d.tipo_dia?.slice(0, 4) || '—'}</span>
                    <span style={{ color: '#85967c', fontSize: 12, minWidth: 48 }}>{fmtFecha(d.fecha)}</span>
                    <span style={{ color: '#efffe3', fontSize: 13, fontWeight: 700, fontFamily: 'monospace', marginLeft: 'auto' }}>
                      {vFmt != null ? vFmt : '—'} <span style={{ fontSize: 10, color: '#baccb0' }}>{metricaActual?.fmtUnit || metricaActual?.unit}</span>
                    </span>
                    <span style={{ color: '#85967c', fontSize: 11, marginLeft: 6 }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                  {/* Detalle expandido */}
                  {isExpanded && d.ejercicios_detalle && (
                    <div style={{ padding: '4px 0 12px 8px' }}>
                      {d.duracion_secs && (
                        <div style={{ color: '#85967c', fontSize: 11, marginBottom: 8 }}>
                          ⏱ {Math.floor(d.duracion_secs/60)}min {d.duracion_secs%60}s · {d.n_ejercicios} ejercicios
                        </div>
                      )}
                      {d.ejercicios_detalle.map((ex, ei) => (
                        <div key={ei} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0', borderTop: '1px solid rgba(255,255,255,0.03)' }}>
                          <span style={{ color: '#efffe3', fontSize: 12, fontWeight: 700, flex: 1 }}>{ex.name}</span>
                          <span style={{ color: '#85967c', fontSize: 11 }}>{ex.series_hechas}/{ex.series_total} series</span>
                          {ex.peso_max > 0 && <span style={{ color: color, fontSize: 11, fontWeight: 700 }}>{ex.peso_max}kg</span>}
                          {ex.volumen > 0 && <span style={{ color: '#baccb0', fontSize: 10 }}>{ex.volumen}kg·rep</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {datos.length > listaLimit && (
              <button
                onClick={() => setListaLimit(l => l + 20)}
                style={{
                  width: '100%', marginTop: 10, padding: '10px',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10, color: '#baccb0', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}
              >
                Ver {Math.min(20, datos.length - listaLimit)} más ({datos.length - listaLimit} restantes)
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorkoutScreen({ todayType, setTodayType, setTab, activeRoutine, setActiveRoutine, onOpenCoach, onCicloAvanzar, settings }) {
  // ── Cuidado y recuperación: descarga y bloqueo de progresión ──────────────
  const descarga = descargaActiva(settings);
  const bloqueo = bloqueoActivo(settings);
  const descargaPct = parseFloat(settings?.descarga_pct) || 30;
  const [exercises, setExercises] = useState(() => initExercises(todayType, activeRoutine));
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [showRoutinesPanel, setShowRoutinesPanel] = useState(false);
  const [rutinas, setRutinas] = useState([]);
  const [loadingRutinas, setLoadingRutinas] = useState(false);
  const activeRoutineRef = React.useRef(activeRoutine);
  React.useEffect(() => { activeRoutineRef.current = activeRoutine; }, [activeRoutine]);
  const [showAddEx, setShowAddEx] = useState(false);
  const [newEx, setNewEx] = useState({ name: '', rest: 120, sets: 3, machine: '', position: '' });
  const [exSuggestions, setExSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedPopup, setSavedPopup] = useState(false);
  const savedIdsRef = useRef(null); // { sesionId, historialId } tras el primer guardado automático
  const [autoStartTimer, setAutoStartTimer] = useState(false);
  const [sessionSecs, setSessionSecs] = useState(0);
  const sessionStartRef = useRef(null);
  const sessionIntervalRef = useRef(null);
  const exerciseTimers = useRef({});

  // ── Restaurar sesión activa al montar ─────────────────────────────────────
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    fetch(`${API}/entrenamiento/sesion-activa?fecha=${today}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (!data || data.fecha !== today) return;
        // Restaurar exercises con nuevos IDs
        const restored = (data.exercises_state || []).map(ex => ({
          ...ex,
          id: `ex_${++_idCounter}`,
          sets: (ex.sets || []).map(s => ({ ...s, id: `set_${++_idCounter}` })),
        }));
        setExercises(restored);
        // Restaurar timers (guardados por nombre de ejercicio)
        const timers = data.timers_state || {};
        restored.forEach(ex => {
          if (timers[ex.name]) exerciseTimers.current[ex.id] = timers[ex.name];
        });
        // Restaurar contador de sesión
        if (data.session_start_ts) {
          sessionStartRef.current = data.session_start_ts;
          setSessionSecs(Math.floor((Date.now() - data.session_start_ts) / 1000));
          setAutoStartTimer(true);
        }
      })
      .catch(() => {});
  }, []);

  // ── Auto-guardar sesión activa ────────────────────────────────────────────
  const saveActiveSession = (currentExercises) => {
    const today = new Date().toISOString().split('T')[0];
    const timersState = {};
    currentExercises.forEach(ex => {
      if (exerciseTimers.current[ex.id]) timersState[ex.name] = exerciseTimers.current[ex.id];
    });
    fetch(`${API}/entrenamiento/sesion-activa`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({
        fecha: today,
        tipo_dia: todayType,
        session_start_ts: sessionStartRef.current,
        exercises_state: currentExercises,
        timers_state: timersState,
      }),
    }).catch(() => {});

    if (activeRoutineRef.current?.id) {
      const cleanEx = currentExercises.map(e => ({
        name: e.name, sets: Math.max(1, e.sets.length), reps: e.reps, weight: e.weight, rest: e.rest,
        machine: e.machine, position: e.position,
        ...(e.es_peso_corporal ? { es_peso_corporal: e.es_peso_corporal } : {}),
      }));
      fetch(`${API}/rutinas/${activeRoutineRef.current.id}/dia_ejercicios`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ dia: todayType, ejercicios: cleanEx }),
      }).catch(() => {});
    }
  };

  // ── Ticker del contador de sesión ─────────────────────────────────────────
  useEffect(() => {
    if (sessionStartRef.current) {
      sessionIntervalRef.current = setInterval(() => {
        setSessionSecs(Math.floor((Date.now() - sessionStartRef.current) / 1000));
      }, 1000);
    }
    return () => clearInterval(sessionIntervalRef.current);
  }, [autoStartTimer]);

  // Re-init cuando cambia el tipo de rutina
  useEffect(() => {
    setExercises(initExercises(todayType, activeRoutineRef.current));
    setSelectedIdx(null);
    setShowAddEx(false);
    exerciseTimers.current = {};
  }, [todayType]);

  // Abrir panel de rutinas — carga lista desde BD
  const openRoutinesPanel = async () => {
    setShowRoutinesPanel(true);
    setLoadingRutinas(true);
    try {
      const r = await fetch(`${API}/rutinas`, { credentials: 'include' });
      const data = await r.json();
      setRutinas(Array.isArray(data) ? data : []);
    } catch {}
    setLoadingRutinas(false);
  };

  // Borrar una rutina
  const handleBorrarRutina = async (id) => {
    try {
      await fetch(`${API}/rutinas/${id}`, { method: 'DELETE', credentials: 'include' });
      setRutinas(prev => prev.filter(r => r.id !== id));
    } catch {}
  };

  // Activar una rutina diferente
  const handleActivarRutina = async (id) => {
    try {
      await fetch(`${API}/rutinas/${id}/activar`, { method: 'POST', credentials: 'include' });
      const r = await fetch(`${API}/rutinas/activa`, { credentials: 'include' });
      const data = await r.json();
      if (data?.id) {
        setActiveRoutine(data);
        const newCiclo = data.ciclo || [];
        const newDayType = newCiclo.includes(todayType) ? todayType : (newCiclo[0] || 'Descanso');
        if (newDayType !== todayType) {
          setTodayType(newDayType);
        } else {
          setExercises(initExercises(newDayType, data));
          setSelectedIdx(null);
          setShowAddEx(false);
          exerciseTimers.current = {};
        }
      }
    } catch {}
    setShowRoutinesPanel(false);
  };

  // Drag & drop
  const [progresoOpen, setProgresoOpen] = React.useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragDeltaY, setDragDeltaY] = useState(0);
  const [dragReady, setDragReady] = useState(null);
  const dragStartY = useRef(0);
  const dragFixedTop = useRef(0);
  const longPressRef = useRef(null);
  const CARD_H = 72;
  const dragIdxRef = useRef(null);
  const dragOverIdxRef = useRef(null);
  const exercisesLenRef = useRef(0);
  const handleEls = useRef({});
  useEffect(() => { dragIdxRef.current = dragIdx; }, [dragIdx]);
  useEffect(() => { dragOverIdxRef.current = dragOverIdx; }, [dragOverIdx]);
  useEffect(() => { exercisesLenRef.current = exercises.length; }, [exercises]);

  // Listeners nativos no-pasivos en los handles — única forma de bloquear selección iOS
  useEffect(() => {
    const cleanups = exercises.map((_, ei) => {
      const el = handleEls.current[ei];
      if (!el) return () => {};
      const onStart = (e) => {
        // NO preventDefault aquí: en iOS bloquea que touchmove llegue al window
        // La selección se bloquea con touch-action:none + WebkitUserSelect:none CSS
        dragStartY.current = e.touches[0].clientY;
        const cardEl = el.closest('[data-exercise-card]') || el;
        setDragReady(ei);
        longPressRef.current = setTimeout(() => {
          dragFixedTop.current = cardEl.getBoundingClientRect().top;
          dragIdxRef.current = ei;
          dragOverIdxRef.current = ei;
          setDragReady(null);
          setDragIdx(ei);
          setDragDeltaY(0);

          // FIX: Los listeners de window se registran AQUÍ, dentro del setTimeout,
          // de forma que están activos en el mismo hilo de ejecución en el que
          // el usuario ya tiene el dedo apoyado.
          const onMove = (ev) => {
            ev.preventDefault();
            const dy = ev.touches[0].clientY - dragStartY.current;
            setDragDeltaY(dy);
            const steps = Math.round(dy / CARD_H);
            const over = Math.max(0, Math.min(exercisesLenRef.current - 1, dragIdxRef.current + steps));
            dragOverIdxRef.current = over;
            setDragOverIdx(over);
          };

          const onEnd = () => {
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
            window.removeEventListener('touchcancel', onEnd);
            setExercises(prev => {
              const di = dragIdxRef.current;
              const doi = dragOverIdxRef.current;
              if (di === null || doi === null || di === doi) return prev;
              const arr = [...prev];
              const [item] = arr.splice(di, 1);
              arr.splice(doi, 0, item);
              return arr;
            });
            setDragIdx(null);
            setDragOverIdx(null);
            setDragDeltaY(0);
            dragIdxRef.current = null;
            dragOverIdxRef.current = null;
          };

          window.addEventListener('touchmove', onMove, { passive: false });
          window.addEventListener('touchend', onEnd);
          window.addEventListener('touchcancel', onEnd);
        }, 380);
      };
      const onEnd = () => { clearTimeout(longPressRef.current); setDragReady(null); };
      el.addEventListener('touchstart', onStart, { passive: false });
      el.addEventListener('touchend', onEnd);
      el.addEventListener('touchcancel', onEnd);
      return () => {
        el.removeEventListener('touchstart', onStart);
        el.removeEventListener('touchend', onEnd);
        el.removeEventListener('touchcancel', onEnd);
      };
    });
    return () => cleanups.forEach(c => c());
  }, [exercises]);

  // ── Actualizar campo de ejercicio ─────────────────────────────────────────
  const updateExerciseField = (field, val) => {
    const exName = exercises[selectedIdx]?.name ?? null;
    // Ejercicios añadidos durante el periodo (no están en el baseline) quedan exentos.
    const exento = exentoBloqueo(settings, exName);
    // Bloqueo de progresión: peso y reps no pueden superar el baseline
    if (bloqueo && !exento && exName && (field === 'weight' || field === 'reps')) {
      const techo = baselineDe(settings, exName, field);
      const nuevo = parseFloat(val);
      if (techo !== null && !isNaN(nuevo) && nuevo > techo) return;
    }
    // Bloqueo: no marcar "subir peso" (es un recordatorio de progresión)
    if (bloqueo && !exento && field === 'subir_peso' && val) return;
    setExercises(prev => prev.map((ex, i) => {
      if (i !== selectedIdx) return ex;
      if (field === 'name') {
        // Renombrar clave localStorage
        if (val && val !== ex.name) {
          const saved = loadExLS(ex.name);
          try {
            localStorage.setItem(`fitapp_ex_${val}`, JSON.stringify(saved));
            localStorage.removeItem(`fitapp_ex_${ex.name}`);
          } catch {}
        }
      } else {
        saveExLS(ex.name, { [field]: val });
      }
      return { ...ex, [field]: val };
    }));
    // Persistir en backend
    const PERSIST_FIELDS = ['machine', 'position', 'weight', 'reps', 'es_peso_corporal', 'name', 'subir_peso'];
    if (PERSIST_FIELDS.includes(field) && activeRoutineRef.current?.id && exName) {
      fetch(`${API}/rutinas/${activeRoutineRef.current.id}/ejercicio`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dia: todayType, accion: 'update_field', nombre: exName, campo: field, valor: String(val) }),
      }).catch(() => {});
    }
    // Backfill historial al renombrar
    if (field === 'name' && val && exName && val !== exName) {
      fetch(`${API}/entrenamiento/ejercicio/renombrar`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nombre_viejo: exName,
          nombre_nuevo: val,
          rutina_id: activeRoutineRef.current?.id ?? null,
          dia: todayType ?? null,
        }),
      }).catch(() => {});
    }
  };

  // ── C5: Sugerencias al añadir ejercicio ───────────────────────────────────
  const handleNewExNameChange = (val) => {
    setNewEx(p => ({ ...p, name: val }));
    if (val.trim().length >= 1) {
      const all = getAllKnownExerciseNames(activeRoutineRef.current);
      const filtered = all.filter(n =>
        n.toLowerCase().includes(val.trim().toLowerCase()) && n.toLowerCase() !== val.trim().toLowerCase()
      );
      setExSuggestions(filtered.slice(0, 8));
    } else {
      setExSuggestions([]);
    }
  };

  const handlePickExSuggestion = (name) => {
    const saved = loadExLS(name);
    setNewEx({ name, rest: saved.rest || 120, sets: 3, machine: saved.machine || '', position: saved.position || '' });
    setExSuggestions([]);
  };

  // ── Marcar/desmarcar set individual ───────────────────────────────────────
  const toggleSet = (si) => {
    const updated = exercises.map((ex, i) => {
      if (i !== selectedIdx) return ex;
      const newSets = ex.sets.map((s, j) => j !== si ? s : { ...s, done: !s.done });
      const allDone = newSets.length > 0 && newSets.every(s => s.done);
      const timer = exerciseTimers.current[ex.id];
      if (allDone && timer?.startedAt && ex.totalSecs == null) {
        return { ...ex, sets: newSets, totalSecs: Math.round((Date.now() - timer.startedAt) / 1000) };
      }
      if (!allDone && ex.totalSecs != null) {
        return { ...ex, sets: newSets, totalSecs: null };
      }
      return { ...ex, sets: newSets };
    });
    setExercises(updated);
    saveActiveSession(updated);
  };

  // ── Marcar primera serie no hecha (llamado desde doble-tap) ───────────────
  const markNextSet = (tiempos = {}) => {
    const updated = exercises.map((ex, i) => {
      if (i !== selectedIdx) return ex;
      let marked = false;
      const newSets = ex.sets.map(s => {
        if (!marked && !s.done) { marked = true; return { ...s, done: true }; }
        return s;
      });
      const allDone = newSets.length > 0 && newSets.every(s => s.done);
      const timer = exerciseTimers.current[ex.id];
      const base = { ...ex, sets: newSets };
      if (allDone) {
        if (timer?.startedAt && ex.totalSecs == null) base.totalSecs = Math.round((Date.now() - timer.startedAt) / 1000);
        if (tiempos.tiempo_ejercicio != null) base.tiempo_ejercicio = tiempos.tiempo_ejercicio;
        if (tiempos.tiempo_recuperacion != null) base.tiempo_recuperacion = tiempos.tiempo_recuperacion;
      }
      return base;
    });
    setExercises(updated);
    saveActiveSession(updated);
  };

  // ── Añadir serie ──────────────────────────────────────────────────────────
  const addSet = () => {
    // Bloqueo de progresión: no superar las series del baseline
    if (bloqueo) {
      const ex = exercises[selectedIdx];
      const baseSets = ex ? baselineDe(settings, ex.name, 'sets') : null;
      if (baseSets !== null && ex.sets.length >= baseSets) return;
    }
    const updated = exercises.map((ex, i) => i !== selectedIdx ? ex : {
      ...ex,
      sets: [...ex.sets, { id: `set_${++_idCounter}`, done: false }],
    });
    setExercises(updated);
    saveActiveSession(updated);
  };

  // ── Actualizar tiempo de intervalo (persiste en rutina) ───────────────────
  const updateRest = (sec) => {
    let exName = null;
    setExercises(prev => prev.map((ex, i) => {
      if (i !== selectedIdx) return ex;
      exName = ex.name;
      saveExLS(ex.name, { rest: sec });
      return { ...ex, rest: sec };
    }));
    if (activeRoutine?.id && exName) {
      fetch(`${API}/rutinas/${activeRoutine.id}/ejercicio`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ dia: todayType, accion: 'update_rest', nombre: exName, descanso: sec }),
      }).catch(() => {});
    }
  };


  // Desplazamiento de las otras tarjetas mientras se arrastra
  const getCardShift = (ei) => {
    if (dragIdx === null || dragOverIdx === null || dragOverIdx === dragIdx) return 0;
    if (dragIdx < dragOverIdx) {
      if (ei > dragIdx && ei <= dragOverIdx) return -CARD_H;
    } else {
      if (ei >= dragOverIdx && ei < dragIdx) return CARD_H;
    }
    return 0;
  };

  // ── Iniciar sesión (botón Go) ──────────────────────────────────────────────
  const startSessionIfNeeded = () => {
    if (!sessionStartRef.current) {
      sessionStartRef.current = Date.now();
      setSessionSecs(0);
        exerciseTimers.current = {};
      setAutoStartTimer(true);
    }
  };

  const handleTimerActivate = () => {
    startSessionIfNeeded();
    const activeId = exercises[selectedIdx]?.id;
    if (activeId && !exerciseTimers.current[activeId]?.startedAt) {
      exerciseTimers.current[activeId] = { ...exerciseTimers.current[activeId], startedAt: Date.now() };
    }
  };

  // Helper: registra startedAt para un ejercicio por índice si aún no tiene
  const recordExStart = (idx) => {
    if (idx == null || idx >= exercises.length) return;
    const exId = exercises[idx]?.id;
    if (exId && !exerciseTimers.current[exId]?.startedAt) {
      exerciseTimers.current[exId] = {
        ...exerciseTimers.current[exId],
        startedAt: Date.now(),
      };
    }
  };

  const handleGo = () => {
    startSessionIfNeeded();
    setSelectedIdx(0);
  };

  // ── Guardar sesión (manual o auto) ────────────────────────────────────────
  const saveSession = async ({ auto = false } = {}) => {
    if (!auto && saving) return;
    if (!auto) setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const duracionSeg = sessionStartRef.current
        ? Math.round((Date.now() - sessionStartRef.current) / 1000)
        : null;
      // En descarga se registra el peso realmente levantado (reducido) en el
      // historial. Peso corporal excluido (pesoDescargaEj devuelve null).
      const exercisesToSave = exercises.map(ex => {
        let w = ex.weight;
        if (descarga) {
          const red = pesoDescargaEj(ex, descargaPct);
          if (red !== null) w = String(red);
        }
        return {
          ...ex,
          weight: w,
          ...(descarga ? { descarga: true } : {}),
          sets: ex.sets.map(s => ({ ...s, reps: ex.reps, weight: w })),
        };
      });

      if (savedIdsRef.current) {
        // Actualizar registros existentes (PUT)
        const { sesionId, historialId } = savedIdsRef.current;
        await fetch(`${API}/sesiones/${sesionId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ ejercicios: exercisesToSave }),
        }).catch(() => {});
        if (historialId) {
          await fetch(`${API}/entrenamiento/guardar/${historialId}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ fecha: today, tipo_dia: todayType, duracion_secs: duracionSeg, ejercicios: exercisesToSave }),
          }).catch(() => {});
        }
      } else {
        // Crear nuevos registros (POST)
        const r1 = await fetch(`${API}/sesiones`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ fecha: today, tipo: todayType, ejercicios: exercisesToSave }),
        }).catch(() => null);
        const sesionData = r1?.ok ? await r1.json() : {};
        const r2 = await fetch(`${API}/entrenamiento/guardar`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ fecha: today, tipo_dia: todayType, duracion_secs: duracionSeg, ejercicios: exercisesToSave, ...(descarga ? { extra: { descarga: true, descarga_pct: descargaPct } } : {}) }),
        }).catch(() => null);
        const historialData = r2?.ok ? await r2.json() : {};
        if (sesionData?.id) savedIdsRef.current = { sesionId: sesionData.id, historialId: historialData?.id };
      }

      // Parar siempre el cronómetro al guardar (auto o manual)
      sessionStartRef.current = null;
      clearInterval(sessionIntervalRef.current);
      setSessionSecs(0);

      if (!auto) {
        // Guardado manual: limpiar además sesión activa y timers
        await fetch(`${API}/entrenamiento/sesion-activa?fecha=${today}`, {
          method: 'DELETE', credentials: 'include',
        }).catch(() => {});
        savedIdsRef.current = null;
        exerciseTimers.current = {};
        setAutoStartTimer(false);
      }
      // Avanzar ciclo SIEMPRE según el día entrenado (el backend reordena el
      // ciclo desde ese día e ignora reentradas del mismo día por idempotencia).
      // Si no se guarda sesión, esto no se llama y el día pendiente se mantiene.
      if (onCicloAvanzar) {
        await onCicloAvanzar(todayType, today);
      }
      setSavedPopup(true);
      setTimeout(() => setSavedPopup(false), auto ? 1500 : 3000);
    } finally {
      if (!auto) setSaving(false);
    }
  };

  // ── Descartar sesión ─────────────────────────────────────────────────────
  const discardSession = async () => {
    if (!window.confirm('¿Descartar la sesión? Se perderán todos los avances y no se guardará nada.')) return;
    const today = new Date().toISOString().split('T')[0];
    // Borrar sesión activa
    fetch(`${API}/entrenamiento/sesion-activa?fecha=${today}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
    // Borrar registros ya guardados si existían
    if (savedIdsRef.current) {
      const { sesionId, historialId } = savedIdsRef.current;
      if (sesionId) fetch(`${API}/sesiones/${sesionId}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
      if (historialId) fetch(`${API}/entrenamiento/guardar/${historialId}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
      savedIdsRef.current = null;
    }
    // Resetear estado local
    exerciseTimers.current = {};
    sessionStartRef.current = null;
    clearInterval(sessionIntervalRef.current);
    setSessionSecs(0);
        exerciseTimers.current = {};
    setAutoStartTimer(false);
    setExercises(initExercises(todayType, activeRoutineRef.current));
  };

    // ── Auto-guardar cuando se completan todos los ejercicios ─────────────────
  useEffect(() => {
    if (selectedIdx !== null) return;
    if (exercises.length === 0) return;
    const allDone = exercises.every(ex => ex.sets.length > 0 && ex.sets.every(s => s.done));
    if (allDone) saveSession({ auto: true });
  }, [selectedIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const style = getDayStyle(todayType);

  return (
    <div data-no-swipe style={{ padding: '0 16px calc(100px + env(safe-area-inset-bottom))' }}>
      {progresoOpen && <ProgresoView onClose={() => setProgresoOpen(false)} API={API} todayType={todayType} />}
      <div style={{ paddingTop: 'calc(var(--sat, env(safe-area-inset-top, 0px)) + 14px)', paddingBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 900, margin: 0, fontFamily: '"Plus Jakarta Sans", sans-serif', letterSpacing: -1, lineHeight: 1.1 }}>Gym</h1>
          <button
            onClick={() => onOpenCoach && onOpenCoach('Quiero crear una nueva rutina de entrenamiento. MODO GUIADO: hazme UNA sola pregunta, espera mi respuesta, evalúala y haz la siguiente. Sé muy preciso para que pueda responder en pocas palabras. Cuando tengas suficiente información, diseña la rutina completa.')}
            style={{
              marginLeft: 'auto',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, padding: '4px 12px',
              color: COLORS.textMuted, fontSize: 11, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >+ Crear rutina</button>
        </div>
        {/* Rutina activa — badge con nombre y botón Progreso */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <span style={{ color: COLORS.textDim, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Rutina:</span>
          <button
            onClick={openRoutinesPanel}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, padding: '4px 10px',
              color: COLORS.textMuted, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {activeRoutine?.nombre || 'Push / Legs / Pull'} <span style={{ opacity: 0.6, fontSize: 10 }}>›</span>
          </button>
          <button
            onClick={() => setProgresoOpen(true)}
            style={{
              marginLeft: 'auto',
              background: `${COLORS.accent}18`,
              border: `1px solid ${COLORS.accent}40`,
              borderRadius: 8, padding: '4px 12px',
              color: COLORS.accent, fontSize: 12, fontWeight: 800,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >📈 Progreso</button>
        </div>
        <DaySelector todayType={todayType} setTodayType={setTodayType} setTab={setTab} onGo={handleGo} sessionSecs={sessionStartRef.current && todayType !== 'Descanso' ? sessionSecs : null} ciclo={activeRoutine?.ciclo} allowExpand={true} />

        {/* Banner de cuidado y recuperación */}
        {descarga && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, background: '#2ecc7114', border: '1px solid #2ecc7145', borderRadius: 12, padding: '10px 14px' }}>
            <span style={{ fontSize: 18 }}>🪶</span>
            <div>
              <div style={{ color: '#2ecc71', fontSize: 13, fontWeight: 800 }}>Semana de descarga — cargas al −{descargaPct}%</div>
              <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 1 }}>Hasta el {fmtFecha(settings.descarga_hasta)} · progresión bloqueada</div>
            </div>
          </div>
        )}
        {!descarga && bloqueo && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, background: '#4fc3f714', border: '1px solid #4fc3f745', borderRadius: 12, padding: '10px 14px' }}>
            <span style={{ fontSize: 18 }}>🔒</span>
            <div>
              <div style={{ color: '#4fc3f7', fontSize: 13, fontWeight: 800 }}>Progresión bloqueada</div>
              <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 1 }}>Sin subir peso, reps ni series hasta el {fmtFecha(settings.bloqueo_progresion_hasta)}</div>
            </div>
          </div>
        )}
      </div>

      {todayType === 'Descanso' && (() => {
        const cycle = activeRoutine?.ciclo || ['Push', 'Pull', 'Legs', 'Descanso'];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', padding: 20 }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>◎</div>
            <h2 style={{ color: COLORS.purple, fontSize: 28, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif", margin: '0 0 8px' }}>Día de descanso</h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.6, fontSize: 14, marginBottom: 28 }}>Tu cuerpo crece mientras descansas.</p>
            <div style={{ width: '100%', maxWidth: 320 }}>
              <p style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 1, textAlign: 'center', marginBottom: 12, textTransform: 'uppercase' }}>¿Quieres cambiar a otro día?</p>
              {cycle.filter(t => t !== 'Descanso').map(type => {
                const s = getDayStyle(type);
                return (
                  <div
                    key={type}
                    onClick={() => setTodayType(type)}
                    style={{ padding: '16px 24px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, background: s.bg, border: `1px solid ${s.color}40`, borderRadius: 16, marginBottom: 10 }}
                  >
                    <span style={{ fontSize: 22, color: s.color }}>{s.icon}</span>
                    <span style={{ color: s.color, fontWeight: 700, fontSize: 17, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{type}</span>
                    <span style={{ marginLeft: 'auto', color: s.color, fontSize: 22 }}>→</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {todayType !== 'Descanso' && <>
      {/* Tarjeta flotante que sigue el dedo */}
      {dragIdx !== null && exercises[dragIdx] && (() => {
        const ex = exercises[dragIdx];
        const isDone = ex.sets.length > 0 && ex.sets.every(s => s.done);
        return (
          <div style={{
            position: 'fixed',
            top: dragFixedTop.current + dragDeltaY,
            left: 16, right: 16,
            maxWidth: 398, margin: '0 auto',
            zIndex: 1000,
            pointerEvents: 'none',
            borderRadius: 14,
            background: `${style.color}30`,
            backdropFilter: 'blur(20px)',
            border: `2px solid ${style.color}`,
            padding: '16px 18px',
            boxShadow: `0 24px 64px rgba(0,0,0,0.8), 0 0 30px ${style.color}40`,
            transform: 'scale(1.04)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: isDone ? COLORS.green : '#fff', margin: 0, fontSize: 15, fontWeight: 700 }}>{ex.name}</h3>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{ex.sets.length} series · {ex.rest || 120}s descanso</span>
              </div>
              <IoReorderTwoOutline size={24} color={style.color} />
            </div>
          </div>
        );
      })()}

      {/* Lista de ejercicios */}
      {exercises.map((ex, ei) => {
        const isDone = ex.sets.length > 0 && ex.sets.every(s => s.done);
        const shift = getCardShift(ei);
        const isReady = dragReady === ei;
        const isDraggingThis = dragIdx === ei;
        return (
          <div key={ex.id} data-exercise-card="true" style={{
            marginBottom: 8,
            transform: isDraggingThis ? undefined : `translateY(${shift}px)`,
            transition: (dragIdx !== null && !isDraggingThis) ? 'transform 0.18s ease' : 'none',
          }}>
            <SwipeableRow onDelete={() => {
              const exName = exercises[ei]?.name;
              setExercises(prev => prev.filter((_, i) => i !== ei));
              if (activeRoutine?.id && exName) {
                fetch(`${API}/rutinas/${activeRoutine.id}/ejercicio`, {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ dia: todayType, accion: 'remove', nombre: exName }),
                }).catch(() => {});
              }
            }}>
              <div
                onClick={() => { if (dragIdx === null) { setSelectedIdx(ei); } }}
                style={{
                  background: isDraggingThis
                    ? `${style.color}08`
                    : (isReady ? `${style.color}18` : isDone ? 'rgba(57,255,20,0.08)' : 'rgba(255,255,255,0.04)'),
                  backdropFilter: isDraggingThis ? undefined : 'blur(10px)',
                  border: isDraggingThis
                    ? `2px dashed ${style.color}50`
                    : `1px solid ${isReady ? style.color + '60' : isDone ? COLORS.green + '40' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: 14, padding: '16px 18px',
                  cursor: isDraggingThis ? 'default' : 'pointer',
                  userSelect: 'none', WebkitUserSelect: 'none',
                  transition: 'background 0.15s, border-color 0.15s',
                  display: 'flex', flexDirection: 'column',
                  minHeight: 72,
                }}
              >
                {/* Fila superior: nombre + iconos */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: isDraggingThis ? 0 : 1 }}>
                  <div>
                    <h3 style={{ color: isDone ? COLORS.green : '#fff', margin: 0, fontSize: 19, fontWeight: 700 }}>{ex.name}</h3>
                    <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                      {ex.sets.filter(s => s.done).length}/{ex.sets.length} series
                      {ex.machine ? ` · Máq. ${ex.machine}` : ''}
                      {' · '}{ex.rest || 120}s
                    </span>
                    {(ex.weight || ex.reps) && (() => {
                      const pesoRed = descarga ? pesoDescargaEj(ex, descargaPct) : null;
                      return (
                        <span style={{ color: style.color, fontSize: 19, fontWeight: 700, display: 'block', marginTop: 3, opacity: 0.85 }}>
                          {ex.weight ? (pesoRed !== null ? (
                            <>
                              <s style={{ opacity: 0.4, fontSize: 15 }}>{ex.weight}</s>
                              {' '}<span style={{ color: '#2ecc71' }}>{pesoRed} kg</span>
                            </>
                          ) : `${ex.weight} kg`) : ''}
                          {ex.weight ? ' · ' : ''}{ex.sets.length} series{ex.reps ? ` · ${ex.reps} reps` : ''}
                          {ex.subir_peso && (!bloqueo || exentoBloqueo(settings, ex.name)) && <span style={{ marginLeft: 6, fontSize: 13, color: '#2ecc71', fontWeight: 800, verticalAlign: 'middle' }}>↑</span>}
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ color: COLORS.green, fontSize: 16, visibility: isDone ? 'visible' : 'hidden' }}>✓</span>
                    <div
                      ref={el => { handleEls.current[ei] = el; }}
                      style={{
                        touchAction: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        WebkitTouchCallout: 'none',
                        padding: '10px 6px',
                        cursor: 'grab',
                        opacity: isDraggingThis ? 0 : 1,
                      }}
                    >
                      <IoReorderTwoOutline size={24} color={isReady ? style.color : COLORS.textDim} style={{ display: 'block', transition: 'color 0.15s' }} />
                    </div>
                  </div>
                </div>

                {/* Fila inferior: tiempo total del ejercicio — mismo estilo que contador de sesión */}
                {isDone && ex.totalSecs != null && !isDraggingThis && (
                  <div style={{
                    marginTop: 6,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    paddingLeft: 2,
                  }}>
                    <span style={{
                      color: style.color,
                      fontSize: 18,
                      fontWeight: 900,
                      fontFamily: 'monospace',
                      letterSpacing: 1,
                    }}>
                      ⏱ {String(Math.floor(ex.totalSecs / 60)).padStart(2, '0')}:{String(ex.totalSecs % 60).padStart(2, '0')}
                    </span>
                  </div>
                )}

              </div>
            </SwipeableRow>
          </div>
        );
      })}

      {/* Añadir ejercicio */}
      {showAddEx ? (
        <div style={{ background: COLORS.card, borderRadius: 16, padding: 18, marginBottom: 14, border: `1px solid ${style.color}40` }}>
          <h3 style={{ color: style.color, margin: '0 0 14px', fontSize: 15 }}>Nuevo ejercicio</h3>

          <div style={{ marginBottom: 10 }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>NOMBRE</label>
            <input
              value={newEx.name}
              onChange={e => handleNewExNameChange(e.target.value)}
              style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 12px', color: COLORS.text, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
            />
            {exSuggestions.length > 0 && (
              <div style={{ marginTop: 4, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {exSuggestions.map(name => {
                  const s = loadExLS(name);
                  return (
                    <div
                      key={name}
                      onClick={() => handlePickExSuggestion(name)}
                      style={{ padding: '9px 14px', borderBottom: `1px solid ${COLORS.border}`, color: COLORS.text, fontSize: 14, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span>{name}</span>
                      {s.machine && <span style={{ color: COLORS.textDim, fontSize: 11 }}>Máq. {s.machine}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>MÁQUINA</label>
              <input value={newEx.machine} onChange={e => setNewEx(p => ({ ...p, machine: e.target.value }))} placeholder="ej: 12A" style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 12px', color: COLORS.text, fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>SERIES</label>
              <input type="number" inputMode="numeric" min="1" value={newEx.sets} onChange={e => setNewEx(p => ({ ...p, sets: Number(e.target.value) }))} style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 12px', color: COLORS.text, fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>POSICIÓN</label>
            <input value={newEx.position} onChange={e => setNewEx(p => ({ ...p, position: e.target.value }))} placeholder="altura sillón, palancas..." style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 12px', color: COLORS.text, fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>DESCANSO (seg)</label>
            <input type="number" inputMode="numeric" min="0" value={newEx.rest} onChange={e => setNewEx(p => ({ ...p, rest: Number(e.target.value) }))} style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 12px', color: COLORS.text, fontSize: 15, outline: 'none', boxSizing: 'border-box' }} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { setShowAddEx(false); setExSuggestions([]); }} style={{ flex: 1, padding: 12, borderRadius: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, cursor: 'pointer', fontWeight: 700 }}>Cancelar</button>
            <button onClick={() => {
              if (!newEx.name.trim()) return;
              const nuevoEj = {
                id: `ex_${++_idCounter}`,
                name: newEx.name.trim(),
                rest: newEx.rest,
                machine: newEx.machine.trim(),
                position: newEx.position.trim(),
                reps: '',
                weight: '',
                sets: Array.from({ length: Math.max(1, newEx.sets) }, () => ({ id: `set_${++_idCounter}`, done: false })),
              };
              setExercises(prev => [...prev, nuevoEj]);
              if (activeRoutine?.id) {
                fetch(`${API}/rutinas/${activeRoutine.id}/ejercicio`, {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({
                    dia: todayType, accion: 'add',
                    ejercicio: { name: nuevoEj.name, sets: Math.max(1, newEx.sets), reps: 10, weight: 0, rest: newEx.rest },
                  }),
                }).catch(() => {});
              }
              setNewEx({ name: '', rest: 120, sets: 3, machine: '', position: '' });
              setExSuggestions([]);
              setShowAddEx(false);
            }} style={{ flex: 2, padding: 12, borderRadius: 12, background: `linear-gradient(135deg, ${style.color}, ${style.color}cc)`, border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 800 }}>AÑADIR</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAddEx(true)} style={{ width: '100%', padding: 14, borderRadius: 14, marginBottom: 14, background: 'none', border: `1px dashed ${style.color}60`, color: style.color, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>+ AÑADIR EJERCICIO</button>
      )}

      <button
        onClick={saveSession}
        disabled={saving}
        style={{ width: '100%', padding: 18, borderRadius: 16, background: saving ? COLORS.bg : `linear-gradient(135deg, ${style.color}, ${style.color}aa)`, border: saving ? `1px solid ${COLORS.border}` : 'none', color: saving ? COLORS.textMuted : '#fff', fontSize: 16, fontWeight: 800, cursor: saving ? 'default' : 'pointer', boxShadow: saving ? 'none' : `0 8px 24px ${style.color}50` }}
      >{saving ? 'Guardando…' : 'GUARDAR SESIÓN'}</button>

      <button
        onClick={discardSession}
        style={{ width: '100%', padding: 14, borderRadius: 14, marginTop: 10, background: 'none', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
      >DESCARTAR SESIÓN</button>

      {/* Modal detalle ejercicio */}
      {selectedIdx !== null && selectedIdx < exercises.length && (
        <Popup type="centered" onClose={() => { setSelectedIdx(null); setAutoStartTimer(false); }} zIndex={9999}>
          <ExerciseModal
            key={selectedIdx}
            ex={exercises[selectedIdx]}
            exIdx={selectedIdx}
            total={exercises.length}
            style={style}
            apiBase={API}
            sessionSecs={sessionStartRef.current && todayType !== 'Descanso' ? sessionSecs : null}
            initialTimerState={exerciseTimers.current[exercises[selectedIdx]?.id] || null}
            onTimerUpdate={(state) => {
              if (exercises[selectedIdx]) {
                exerciseTimers.current[exercises[selectedIdx].id] = { ...(exerciseTimers.current[exercises[selectedIdx].id] || {}), ...state };
              }
            }}
            onStartSession={handleTimerActivate}
            onClose={() => { setSelectedIdx(null); setAutoStartTimer(false); }}
            onUpdateField={updateExerciseField}
            onToggleSet={toggleSet}
            onMarkNextSet={markNextSet}
            onDeleteSet={(si) => {
              const updated = exercises.map((ex, i) => i !== selectedIdx ? ex : {
                ...ex, sets: ex.sets.filter((_, j) => j !== si),
              });
              setExercises(updated);
              saveActiveSession(updated);
            }}
            onAddSet={addSet}
            onUpdateRest={updateRest}
            recovery={{ descarga, bloqueo, pct: descargaPct, baseline: settings?.bloqueo_baseline || null }}
            onBuscarAlternativa={onOpenCoach ? () => {
              const nombre = exercises[selectedIdx]?.name || '';
              setSelectedIdx(null);
              setAutoStartTimer(false);
              onOpenCoach(`Estoy en periodo de descarga (cargas reducidas un ${descargaPct}%). El ejercicio "${nombre}" es de peso corporal, así que no puedo reducir su carga con el porcentaje. Sugiéreme 1-2 alternativas en máquina que trabajen los mismos músculos con carga ligera, indicando un peso inicial orientativo acorde a la descarga.`);
            } : null}
          />
        </Popup>
      )}

      {/* Popup confirmación guardado */}
      {savedPopup && (
        <Popup type="centered" onClose={() => setSavedPopup(false)} zIndex={9999}>
          <div style={{ textAlign: 'center', padding: '12px 8px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h3 style={{ color: COLORS.text, margin: '0 0 8px', fontSize: 18, fontWeight: 800 }}>Sesión guardada</h3>
            <p style={{ color: COLORS.textMuted, margin: '0 0 20px', fontSize: 14 }}>Tu entrenamiento ha sido registrado correctamente.</p>
            <button onClick={() => setSavedPopup(false)} style={{ width: '100%', padding: 14, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: 'none', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 15 }}>CERRAR</button>
          </div>
        </Popup>
      )}

      {/* Panel de rutinas */}
      {showRoutinesPanel && (
        <Popup type="centered" onClose={() => setShowRoutinesPanel(false)} zIndex={9999}>
          <div style={{ padding: '16px 0 8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
              <h3 style={{ color: COLORS.text, margin: 0, fontSize: 16, fontWeight: 800 }}>Mis Rutinas</h3>
              <button onClick={() => setShowRoutinesPanel(false)} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            {loadingRutinas ? (
              <div style={{ textAlign: 'center', padding: '32px 20px', color: COLORS.textMuted, fontSize: 13 }}>Cargando…</div>
            ) : (
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {rutinas.map(r => {
                  const isActive = r.activa;
                  const inner = (
                    <div
                      onClick={() => !isActive && handleActivarRutina(r.id)}
                      style={{
                        padding: '14px 20px',
                        borderBottom: `1px solid ${COLORS.border}`,
                        cursor: isActive ? 'default' : 'pointer',
                        background: isActive ? 'rgba(253,139,0,0.07)' : 'transparent',
                        display: 'flex', alignItems: 'center', gap: 12,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ color: isActive ? COLORS.accent : COLORS.text, fontWeight: 700, fontSize: 15 }}>{r.nombre}</div>
                        <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 2 }}>
                          {(r.ciclo || []).join(' · ')}
                        </div>
                      </div>
                      {isActive && (
                        <span style={{ color: COLORS.accent, fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>✓ ACTIVA</span>
                      )}
                    </div>
                  );
                  return (
                    <div key={r.id}>
                      {isActive
                        ? inner
                        : <SwipeableRow onDelete={() => handleBorrarRutina(r.id)}>{inner}</SwipeableRow>
                      }
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ padding: '14px 20px 8px' }}>
              <button
                onClick={() => { setShowRoutinesPanel(false); onOpenCoach && onOpenCoach('Quiero crear una nueva rutina de entrenamiento. MODO GUIADO: hazme UNA sola pregunta, espera mi respuesta, evalúala y haz la siguiente. Sé muy preciso para que pueda responder en pocas palabras. Cuando tengas suficiente información, diseña la rutina completa.'); }}
                style={{
                  width: '100%', padding: 13, borderRadius: 12,
                  background: 'linear-gradient(135deg, rgba(255,107,53,0.15), rgba(255,51,102,0.15))',
                  border: '1px solid rgba(255,107,53,0.35)',
                  color: '#ff6b35', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                }}
              >
                ⚡ Nueva rutina con FitCoach
              </button>
            </div>
          </div>
        </Popup>
      )}
      </>}
    </div>
  );
}

// AudioContext persistente — se crea una vez y se desbloquea con el primer gesto del usuario
let _audioCtx = null;
function _getAudioCtx() {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
  }
  return _audioCtx;
}
function unlockAudio() {
  const ctx = _getAudioCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
}
function beep() {
  try {
    const ctx = _getAudioCtx();
    if (!ctx) return;
    const play = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(1.0, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    };
    if (ctx.state === 'suspended') { ctx.resume().then(play).catch(() => {}); } else { play(); }
  } catch {}
}

function ExerciseModal({ ex, exIdx, total, style, initialTimerState, onTimerUpdate, onStartSession, onClose, onUpdateField, onToggleSet, onMarkNextSet, onDeleteSet, onAddSet, onUpdateRest, apiBase, recovery, onBuscarAlternativa, sessionSecs }) {
  const { descarga = false, bloqueo = false, pct = 30 } = recovery || {};
  // Ejercicio añadido durante el periodo (no está en el baseline) → exento de bloqueos.
  const bloqueoEx = bloqueo && !exentoBloqueoBaseline(recovery?.baseline, ex.name);
  // Peso corporal excluido de la descarga (pesoDescargaEj devuelve null)
  const pesoRed = descarga ? pesoDescargaEj(ex, pct) : null;
  const baseSets = bloqueo && recovery?.baseline?.[ex.name] ? parseFloat(recovery.baseline[ex.name].sets) : null;
  const setsAlLimite = baseSets !== null && !isNaN(baseSets) && ex.sets.length >= baseSets;
  const storageKey = `fitapp_rest_${ex.name}`;
  const [restDuration, setRestDuration] = useState(() => parseInt(localStorage.getItem(storageKey)) || ex.rest || 120);
  // Timer: 'ready' | 'active' | 'resting'
  const [phase, setPhase] = useState(() => initialTimerState?.phase || 'ready');
  const [setStartedAt, setSetStartedAt] = useState(() => initialTimerState?.setStartedAt || null);
  const [exerciseEndElapsed, setExerciseEndElapsed] = useState(() => initialTimerState?.exerciseEndElapsed || 0);
  const [acumEjercicio, setAcumEjercicio] = useState(() => initialTimerState?.acumEjercicio || 0);
  const [acumDescanso, setAcumDescanso] = useState(() => initialTimerState?.acumDescanso || 0);
  const [tick, setTick] = useState(0);
  const [showTimerEdit, setShowTimerEdit] = useState(false);
  const [deleteMode, setDeleteMode] = useState(null);
  const [paused, setPaused] = useState(false);
  const timerStarted = phase !== 'ready';
  // C2: nombre editable
  const [editName, setEditName] = useState(ex.name);
  useEffect(() => {
    setEditName(ex.name);
    setHistorialData(null);
    setShowHistorial(false);
  }, [ex.name]);
  // Técnica (ExerciseDB) — oculto, pendiente de depurar
  const [showTecnica, setShowTecnica] = useState(false);
  const [tecnicaData, setTecnicaData] = useState(null);
  const [tecnicaLoading, setTecnicaLoading] = useState(false);
  const [tecnicaError, setTecnicaError] = useState(null);
  // Peso corporal
  const [esPesoCorporal, setEsPesoCorporal] = useState(() => ex.es_peso_corporal === true || ex.es_peso_corporal === 'true');
  const togglePesoCorporal = () => {
    const nuevo = !esPesoCorporal;
    setEsPesoCorporal(nuevo);
    onUpdateField('es_peso_corporal', nuevo);
  };
  // Historial de cambios del ejercicio
  const [showHistorial, setShowHistorial] = useState(false);
  const [historialData, setHistorialData] = useState(null);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [histMetrica, setHistMetrica] = useState('peso_max');
  const [histPeriodoDays, setHistPeriodoDays] = useState(0);
  const histChartRef = useRef(null);
  const histChartInstanceRef = useRef(null);
  // C4: notas
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(() => loadExLS(ex.name).notes || '');
  // Fotos de máquina
  const [exPhotos, setExPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const photoInputRef = useRef(null);
  const setLongRef = useRef(null);
  const intervalRef = useRef(null);
  const timerLongRef = useRef(null);
  const tapTimerRef = useRef(null);
  const lastTapRef = useRef(0);
  const pauseStartRef = useRef(null);
  const notifiedRestRef = useRef(false);
  const wakeLockRef = useRef(null);
  const subirPesoRef = useRef(null);
  useEffect(() => {
    const el = subirPesoRef.current;
    if (!el) return;
    const handler = e => { e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('touchstart', handler, { passive: false });
    return () => el.removeEventListener('touchstart', handler, { passive: false });
  }, []);

  const allDone = ex.sets.length > 0 && ex.sets.every(s => s.done);
  const elapsed = (phase !== 'ready' && !allDone && setStartedAt)
    ? Math.floor((Date.now() - setStartedAt) / 1000)
    : 0;
  const restElapsed = phase === 'resting' ? elapsed - exerciseEndElapsed : 0;
  const restRemaining = restDuration - restElapsed; // cuenta atrás del descanso (negativo si se excede)
  const isOverRest = phase === 'resting' && restRemaining <= 0;

  // ── Interval: re-render cada 500ms mientras timer activo ─────────────────
  useEffect(() => {
    if (phase === 'ready' || paused || ex.sets.every(s => s.done)) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => setTick(t => t + 1), 500);
    return () => clearInterval(intervalRef.current);
  }, [phase, paused, ex.sets]);

  // ── Reportar estado del timer al padre para persistirlo ──────────────────
  useEffect(() => {
    onTimerUpdate?.({ phase, setStartedAt, exerciseEndElapsed, acumEjercicio, acumDescanso });
  }, [phase, setStartedAt, exerciseEndElapsed, acumEjercicio, acumDescanso]);

  // ── Notificación silenciosa al alcanzar el límite de descanso ────────────
  useEffect(() => { if (phase === 'resting') notifiedRestRef.current = false; }, [phase]);
  useEffect(() => {
    if (isOverRest && !notifiedRestRef.current) {
      notifiedRestRef.current = true;
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('FitApp — Descanso completado', { body: ex.name, silent: true });
      }
    }
  }, [isOverRest, ex.name]);

  // ── Visibilidad ───────────────────────────────────────────────────────────
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') setTick(t => t + 1); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // ── WakeLock ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!('wakeLock' in navigator)) return;
    if (phase === 'ready') {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      return;
    }
    const requestLock = async () => {
      if (document.visibilityState === 'visible') {
        try { wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch {}
      }
    };
    requestLock();
    document.addEventListener('visibilitychange', requestLock);
    return () => {
      document.removeEventListener('visibilitychange', requestLock);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [phase]);



  // Cargar fotos al montar
  useEffect(() => {
    if (!apiBase || !ex.name) return;
    fetch(`${apiBase}/entrenamiento/ejercicio-fotos?nombre=${encodeURIComponent(ex.name)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setExPhotos(data); })
      .catch(() => {});
  }, [ex.name, apiBase]);

  // ── Handlers del timer ───────────────────────────────────────────────────
  const pendingCount = ex.sets.filter(s => !s.done).length;

  const handleIniciar = () => {
    unlockAudio();
    onStartSession?.();
    setPhase('active');
    setSetStartedAt(Date.now());
    setPaused(false);
    pauseStartRef.current = null;
  };

  const handleDescanso = () => {
    const cur = Math.floor((Date.now() - setStartedAt) / 1000);
    setExerciseEndElapsed(cur);
    setAcumEjercicio(a => a + cur);
    onMarkNextSet();
    setPhase('resting');
    setPaused(false);
    pauseStartRef.current = null;
  };

  const handleProxSerie = () => {
    const cur = Math.floor((Date.now() - setStartedAt) / 1000);
    setAcumDescanso(a => a + (cur - exerciseEndElapsed));
    setSetStartedAt(Date.now());
    setExerciseEndElapsed(0);
    setPhase('active');
    setPaused(false);
    pauseStartRef.current = null;
  };

  const handleTogglePause = () => {
    if (phase === 'ready') return;
    if (paused) {
      const pauseDuration = Date.now() - pauseStartRef.current;
      setSetStartedAt(s => s + pauseDuration);
      pauseStartRef.current = null;
      setPaused(false);
    } else {
      pauseStartRef.current = Date.now();
      setPaused(true);
    }
  };

  const handleReset = () => {
    if (phase === 'ready') return;
    pauseStartRef.current = null;
    setPaused(false);
    setSetStartedAt(Date.now());
    setExerciseEndElapsed(0);
    if (phase === 'resting') setPhase('active');
  };

  const handleCounterTap = () => {
    if (phase === 'ready') return;
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      clearTimeout(tapTimerRef.current);
      lastTapRef.current = 0;
      handleReset();
    } else {
      lastTapRef.current = now;
      tapTimerRef.current = setTimeout(() => {
        lastTapRef.current = 0;
        handleTogglePause();
      }, 300);
    }
  };

  const handleCompletado = () => {
    const cur = setStartedAt ? Math.floor((Date.now() - setStartedAt) / 1000) : 0;
    const finalDescanso = acumDescanso + (phase === 'resting' ? cur - exerciseEndElapsed : 0);
    onMarkNextSet({ tiempo_ejercicio: acumEjercicio, tiempo_recuperacion: finalDescanso });
    onClose();
  };

  const handleAcabar = () => {
    const cur = setStartedAt ? Math.floor((Date.now() - setStartedAt) / 1000) : 0;
    onMarkNextSet({ tiempo_ejercicio: acumEjercicio + cur, tiempo_recuperacion: acumDescanso });
    onClose();
  };

  const nextBtnLabel = phase === 'ready' ? 'INICIAR'
    : phase === 'active' && pendingCount <= 1 ? 'COMPLETADO'
    : phase === 'active' ? 'DESCANSO'
    : pendingCount === 0 ? 'COMPLETADO'
    : 'PRÓX. SERIE';
  const onNextClick = phase === 'ready' ? handleIniciar
    : phase === 'active' && pendingCount <= 1 ? handleAcabar
    : phase === 'active' ? handleDescanso
    : pendingCount === 0 ? handleCompletado
    : handleProxSerie;

  const handleTimerLongStart = () => {
    timerLongRef.current = setTimeout(() => setShowTimerEdit(true), 600);
  };
  const handleTimerLongEnd = () => clearTimeout(timerLongRef.current);

  const applyRestChange = (delta) => {
    const newDur = Math.max(15, restDuration + delta);
    setRestDuration(newDur);
    localStorage.setItem(storageKey, String(newDur));
    onUpdateRest(newDur);
  };

  // ── Display ────────────────────────────────────────────────────────────────
  const restLabel = String(Math.floor(restDuration / 60)) + ':' + String(restDuration % 60).padStart(2, '0');
  // En descanso el contador es una cuenta atrás desde restDuration; al excederse se muestra en negativo
  const timerSecs = phase === 'resting' ? Math.abs(restRemaining) : elapsed;
  const timerSign = phase === 'resting' && restRemaining < 0 ? '-' : '';
  const timerLabel = timerSign + String(Math.floor(timerSecs / 60)).padStart(2, '0') + ':' + String(timerSecs % 60).padStart(2, '0');
  const counterColor = (phase === 'ready' || allDone) ? COLORS.textMuted
    : phase === 'active' ? style.color
    : isOverRest ? '#ff2d55' : '#2ecc71';
  const counterBlink = isOverRest && tick % 2 === 0;

  const doneCount = ex.sets.filter(s => s.done).length;

  const getRejectedTecnicas = () => {
    try { return JSON.parse(localStorage.getItem('fitapp_rejected_tecnicas') || '{}'); } catch { return {}; }
  };
  const addRejectedTecnica = (exerciseName, tecnicaId) => {
    const rejected = getRejectedTecnicas();
    if (!rejected[exerciseName]) rejected[exerciseName] = [];
    if (tecnicaId && !rejected[exerciseName].includes(tecnicaId)) {
      rejected[exerciseName].push(tecnicaId);
      localStorage.setItem('fitapp_rejected_tecnicas', JSON.stringify(rejected));
    }
  };

  const loadTecnica = async (searchQuery = null) => {
    if (showTecnica && !searchQuery) { setShowTecnica(false); return; }
    if (tecnicaData || tecnicaLoading) { setShowTecnica(true); return; }
    setTecnicaLoading(true);
    setShowTecnica(true);
    setTecnicaError(null);
    try {
      const res = await fetch(`${apiBase}/ejercicios/gif?q=${encodeURIComponent(searchQuery || ex.name)}`, { credentials: 'include' });
      const data = await res.json();
      const rejected = getRejectedTecnicas()[ex.name] || [];
      const valid = data.find(d => !rejected.includes(d.id));
      if (valid) {
        setTecnicaData(valid);
        setTecnicaError(null);
      } else if (data.length > 0) {
        setTecnicaError('No se encontraron mas resultados.');
        setTecnicaData(null);
      } else {
        setTecnicaData(null);
        setTecnicaError(data.error || 'No hay datos de tecnica para este ejercicio');
      }
    } catch { setTecnicaError('Error al cargar la tecnica'); }
    setTecnicaLoading(false);
  };

  const HIST_METRICAS = [
    { key: 'peso_max', label: 'Peso', unit: 'kg' },
    { key: 'reps_avg', label: 'Reps', unit: '' },
    { key: 'n_series', label: 'Series', unit: '' },
  ];
  const HIST_PERIODOS = [
    { label: 'Mes', days: 30 },
    { label: 'Año', days: 365 },
    { label: 'Todo', days: 0 },
  ];

  const loadHistorial = async () => {
    if (showHistorial) { setShowHistorial(false); return; }
    setShowHistorial(true);
    if (historialData !== null) return;
    setHistorialLoading(true);
    try {
      const res = await fetch(`${apiBase}/entrenamiento/progreso/ejercicio?nombre=${encodeURIComponent(ex.name)}`, { credentials: 'include' });
      const data = await res.json();
      setHistorialData(Array.isArray(data) ? data : []);
    } catch { setHistorialData([]); }
    setHistorialLoading(false);
  };

  useEffect(() => {
    if (!showHistorial || !histChartRef.current || !historialData?.length) return;
    if (histChartInstanceRef.current) { histChartInstanceRef.current.destroy(); histChartInstanceRef.current = null; }

    const hoy = new Date().toISOString().slice(0, 10);
    const desde = histPeriodoDays > 0
      ? (() => { const d = new Date(); d.setDate(d.getDate() - histPeriodoDays); return d.toISOString().slice(0, 10); })()
      : null;
    const filtered = desde ? historialData.filter(d => d.fecha >= desde) : historialData;
    if (!filtered.length) return;

    const allEntries = [];
    if (desde && filtered[0].fecha > desde) allEntries.push({ fecha: desde, _b: true });
    allEntries.push(...filtered);
    if (filtered[filtered.length - 1].fecha < hoy) allEntries.push({ fecha: hoy, _b: true });

    const labels = allEntries.map(d => { const [, m, dd] = d.fecha.split('-'); return `${dd}/${m}`; });
    const values = allEntries.map(d => d._b ? null : (d[histMetrica] ?? null));
    const metDef = HIST_METRICAS.find(m => m.key === histMetrica);
    const color = style.color;

    const nonNull = values.filter(v => v != null);
    const dMin = nonNull.length ? Math.min(...nonNull) : 0;
    const dMax = nonNull.length ? Math.max(...nonNull) : 0;
    const pad = (dMax - dMin) > 0 ? (dMax - dMin) * 0.15 : Math.max(dMin * 0.1, 2);

    import('chart.js').then(({ Chart, registerables }) => {
      Chart.register(...registerables);
      if (!histChartRef.current) return;
      histChartInstanceRef.current = new Chart(histChartRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            data: values,
            borderColor: color,
            backgroundColor: `${color}20`,
            pointBackgroundColor: allEntries.map(d => d._b ? 'transparent' : color),
            pointBorderColor: allEntries.map(d => d._b ? 'transparent' : color),
            pointRadius: allEntries.map(d => d._b ? 0 : 4),
            pointHoverRadius: allEntries.map(d => d._b ? 0 : 6),
            borderWidth: 2,
            tension: 0.3,
            fill: true,
            spanGaps: true,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              filter: item => !allEntries[item.dataIndex]?._b && item.parsed.y != null,
              callbacks: { label: ctx => `${ctx.parsed.y} ${metDef?.unit || ''}` },
              backgroundColor: '#1c1b1b',
              titleColor: '#efffe3',
              bodyColor: '#baccb0',
              borderColor: 'rgba(255,255,255,0.1)',
              borderWidth: 1,
            },
          },
          scales: {
            x: { ticks: { color: '#85967c', font: { size: 10 }, maxTicksLimit: 7 }, grid: { color: 'rgba(255,255,255,0.04)' } },
            y: { min: Math.max(0, dMin - pad), max: dMax + pad, ticks: { color: '#baccb0', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.06)' } },
          },
        },
      });
    });
    return () => { if (histChartInstanceRef.current) { histChartInstanceRef.current.destroy(); histChartInstanceRef.current = null; } };
  }, [historialData, histMetrica, histPeriodoDays, showHistorial]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !apiBase) return;
    setUploadingPhoto(true);
    try {
      const resized = await resizeImage(file);
      const fd = new FormData();
      fd.append('nombre', ex.name);
      fd.append('file', resized);
      const res = await fetch(`${apiBase}/entrenamiento/ejercicio-foto`, {
        method: 'POST', credentials: 'include', body: fd,
      }).then(r => r.json());
      if (res.id) setExPhotos(p => [...p, res]);
    } catch {}
    setUploadingPhoto(false);
    e.target.value = '';
  };

  const handleDeletePhoto = async (foto) => {
    if (!apiBase) return;
    await fetch(`${apiBase}/entrenamiento/ejercicio-foto/${foto.id}`, {
      method: 'DELETE', credentials: 'include',
    }).catch(() => {});
    setExPhotos(p => p.filter(f => f.id !== foto.id));
  };

  return (
    <div style={{ padding: '24px 20px 32px' }}>
      {/* Header — C2: nombre editable */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ flex: 1, marginRight: 12 }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>EJERCICIO {exIdx + 1} / {total}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onBlur={() => { if (editName.trim() && editName !== ex.name) onUpdateField('name', editName.trim()); }}
              style={{ fontSize: 22, fontWeight: 900, color: COLORS.text, background: 'none', border: 'none', borderBottom: `1px solid rgba(255,255,255,0.15)`, outline: 'none', flex: 1, minWidth: 0, padding: '2px 0', lineHeight: 1.2, fontFamily: 'inherit' }}
            />
            <button
              onClick={togglePesoCorporal}
              style={{ flexShrink: 0, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, cursor: 'pointer', border: esPesoCorporal ? `1px solid ${style.color}80` : '1px solid rgba(255,255,255,0.12)', background: esPesoCorporal ? `${style.color}25` : 'rgba(255,255,255,0.05)', color: esPesoCorporal ? style.color : COLORS.textMuted, letterSpacing: 0.3, whiteSpace: 'nowrap' }}
            >🏋️ PC</button>
          </div>
        </div>
        <div style={{ position: 'relative', flexShrink: 0, alignSelf: 'stretch' }}>
          {sessionSecs !== null && sessionSecs !== undefined && (
            <span style={{ position: 'absolute', top: -18, right: 0, color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>
              ⏱ {String(Math.floor(sessionSecs / 60)).padStart(2, '0')}:{String(sessionSecs % 60).padStart(2, '0')}
            </span>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1, paddingTop: 4 }}>✕</button>
        </div>
      </div>

      {/* Campos: Máquina + Posición + Notas + Técnica */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: (showNotes || showTecnica) ? 8 : 16 }}>
        <div>
          <label style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Máquina</label>
          <input
            value={ex.machine || ''}
            onChange={e => onUpdateField('machine', e.target.value)}
            placeholder="ej: 12A"
            style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '9px 8px', color: '#fff', fontSize: 14, fontWeight: 700, outline: 'none', boxSizing: 'border-box', textAlign: 'center' }}
          />
        </div>
        <div>
          <label style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Posición</label>
          <input
            value={ex.position || ''}
            onChange={e => onUpdateField('position', e.target.value)}
            placeholder="–"
            style={{ width: '100%', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '9px 8px', color: '#fff', fontSize: 14, fontWeight: 700, outline: 'none', boxSizing: 'border-box', textAlign: 'center' }}
          />
        </div>
        <div>
          <label style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Notas</label>
          <button
            onClick={() => setShowNotes(s => !s)}
            style={{ width: '100%', padding: '9px 0', borderRadius: 10, background: showNotes ? '#ff2d5533' : notes ? `${style.color}18` : COLORS.bg, border: `1px solid ${showNotes ? '#ff2d55' : notes ? style.color + '50' : COLORS.border}`, color: showNotes ? '#ff2d55' : notes ? style.color : COLORS.textMuted, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >📝</button>
        </div>
        <div>
          <label style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Historial</label>
          <button
            onClick={() => loadHistorial()}
            style={{ width: '100%', padding: '9px 0', borderRadius: 10, background: showHistorial ? `${style.color}30` : COLORS.bg, border: `1px solid ${showHistorial ? style.color : COLORS.border}`, color: showHistorial ? style.color : COLORS.textMuted, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >{historialLoading ? '⏳' : '📋'}</button>
        </div>
      </div>
      {showTecnica && ReactDOM.createPortal(
        <Popup type="centered" onClose={() => setShowTecnica(false)} zIndex={10002} flex={true}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
            <h3 style={{ color: style.color, margin: 0, fontSize: 18, fontWeight: 800 }}>Técnica: {ex.name}</h3>
            <button onClick={() => setShowTecnica(false)} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: '4px' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', minHeight: 0 }}>
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, overflow: 'hidden', border: `1px solid ${style.color}30` }}>
              {tecnicaLoading && <div style={{ padding: 16, color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>Cargando técnica...</div>}
              {!tecnicaLoading && !tecnicaData && <div style={{ padding: 16, color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>{tecnicaError || "No hay datos de técnica para este ejercicio"}</div>}
              {!tecnicaLoading && tecnicaData && (
                <div>
                  {(tecnicaData.img || tecnicaData.img2) && (() => {
                    const imgs = [tecnicaData.img, tecnicaData.img2].filter(Boolean);
                    const toSrc = (p) => p.startsWith('/') && apiBase && apiBase.includes('http') ? new URL(apiBase).origin + p : p;
                    return (
                      <div style={{ overflowX: 'auto', overflowY: 'hidden', display: 'flex', scrollSnapType: 'x mandatory', background: '#fff', WebkitOverflowScrolling: 'touch' }}>
                        {imgs.map((p, i) => (
                          <img
                            key={i}
                            src={toSrc(p)}
                            alt={tecnicaData.nombre}
                            style={{ flexShrink: 0, width: '100%', objectFit: 'contain', display: 'block', scrollSnapAlign: 'start' }}
                          />
                        ))}
                      </div>
                    );
                  })()}
                  <div style={{ padding: '16px 14px 24px' }}>
                    <div style={{ color: style.color, fontSize: 12, fontWeight: 800, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {tecnicaData.categoria} · {tecnicaData.equipo}
                    </div>
                    {tecnicaData.musculos && JSON.parse(tecnicaData.musculos).length > 0 && (
                      <div style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 12, fontWeight: 700 }}>
                        💪 {JSON.parse(tecnicaData.musculos).join(', ')}
                      </div>
                    )}
                    {tecnicaData.instrucciones && (() => {
                      const steps = JSON.parse(tecnicaData.instrucciones);
                      return steps.length > 0 ? (
                        <ol style={{ margin: 0, paddingLeft: 20, color: COLORS.text, fontSize: 14, lineHeight: 1.6 }}>
                          {steps.map((s, i) => <li key={i} style={{ marginBottom: 8 }}>{s}</li>)}
                        </ol>
                      ) : null;
                    })()}
                    <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${style.color}20` }}>
                      <button onClick={() => { if (tecnicaData.id) addRejectedTecnica(ex.name, tecnicaData.id); loadTecnica(); }} style={{ flex: 1, background: 'rgba(255,100,100,0.15)', border: '1px solid rgba(255,100,100,0.3)', borderRadius: 10, padding: '10px 8px', color: '#ff8080', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>No es correcto</button>
                      <button onClick={() => { if (tecnicaData.id) addRejectedTecnica(ex.name, tecnicaData.id); loadTecnica(ex.name + ' form'); }} style={{ flex: 1, background: 'rgba(255,200,100,0.15)', border: '1px solid rgba(255,200,100,0.3)', borderRadius: 10, padding: '10px 8px', color: '#ffc864', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Buscar de nuevo</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Popup>,
        document.body
      )}
      {showHistorial && ReactDOM.createPortal(
        <Popup type="centered" onClose={() => setShowHistorial(false)} zIndex={10002} flex={true}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
            <h3 style={{ color: style.color, margin: 0, fontSize: 18, fontWeight: 800 }}>Historial: {ex.name}</h3>
            <button onClick={() => setShowHistorial(false)} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: '4px' }}>✕</button>
          </div>

          {/* Selectores — fijos, fuera del scroll */}
          {!historialLoading && historialData?.length > 0 && (
            <div style={{ flexShrink: 0, marginBottom: 10 }}>
              {/* Selector de métrica */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {HIST_METRICAS.map(m => (
                  <button key={m.key} onClick={() => setHistMetrica(m.key)} style={{
                    flex: 1, padding: '6px 0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none',
                    background: histMetrica === m.key ? style.color : 'rgba(255,255,255,0.07)',
                    color: histMetrica === m.key ? '#111' : COLORS.textMuted,
                  }}>{m.label}</button>
                ))}
              </div>
              {/* Selector de período */}
              <div style={{ display: 'flex', gap: 6 }}>
                {HIST_PERIODOS.map(p => (
                  <button key={p.label} onClick={() => setHistPeriodoDays(p.days)} style={{
                    flex: 1, padding: '6px 0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none',
                    background: histPeriodoDays === p.days ? `${style.color}30` : 'rgba(255,255,255,0.05)',
                    color: histPeriodoDays === p.days ? style.color : COLORS.textMuted,
                  }}>{p.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Gráfica — altura fija, fuera del scroll */}
          {!historialLoading && historialData?.length > 0 && (
            <div style={{ flexShrink: 0, height: 180, marginBottom: 16, position: 'relative' }}>
              <canvas ref={histChartRef} style={{ width: '100%', height: '100%' }} />
            </div>
          )}

          {/* Tabla de cambios — scrollable */}
          <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', minHeight: 0 }}>
            {historialLoading && (
              <div style={{ padding: 24, color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>Cargando historial...</div>
            )}
            {!historialLoading && historialData !== null && historialData.length === 0 && (
              <div style={{ padding: 24, color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>No hay sesiones registradas para este ejercicio.</div>
            )}
            {!historialLoading && historialData && historialData.length > 0 && (() => {
              const rows = historialData.reduce((acc, item, i) => {
                const prev = i > 0 ? historialData[i - 1] : null;
                if (!prev) return acc;
                const cambios = [];
                if (item.n_series !== prev.n_series) cambios.push({ campo: 'Series', de: prev.n_series, a: item.n_series });
                if (item.peso_max !== prev.peso_max) cambios.push({ campo: 'Peso', de: prev.peso_max + ' kg', a: item.peso_max + ' kg' });
                if (item.reps_avg !== prev.reps_avg) cambios.push({ campo: 'Reps', de: prev.reps_avg, a: item.reps_avg });
                if (cambios.length > 0) acc.push({ ...item, cambios });
                return acc;
              }, []).reverse();
              return (
                <>
                  <div style={{ color: COLORS.textDim, fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Cambios registrados</div>
                  {rows.length === 0
                    ? <div style={{ padding: '12px 0', color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>Sin cambios registrados en este ejercicio.</div>
                    : (
                      <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, overflow: 'hidden', border: `1px solid ${style.color}30` }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '80px 44px 60px 44px 1fr', gap: 0, padding: '8px 12px', borderBottom: `1px solid ${style.color}20`, background: `${style.color}12` }}>
                          {['Fecha', 'Ser.', 'Peso', 'Reps', 'Cambios'].map(h => (
                            <div key={h} style={{ color: style.color, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
                          ))}
                        </div>
                        {rows.map((row, i) => (
                          <div key={row.fecha + i} style={{
                            display: 'grid', gridTemplateColumns: '80px 44px 60px 44px 1fr', gap: 0,
                            padding: '10px 12px', alignItems: 'center',
                            borderBottom: i < rows.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                            background: `${style.color}08`,
                          }}>
                            <div style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 600 }}>{fmtFecha(row.fecha)}</div>
                            <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}>{row.n_series}</div>
                            <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}>{row.peso_max} kg</div>
                            <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}>{row.reps_avg}</div>
                            <div style={{ fontSize: 11, lineHeight: 1.4 }}>
                              {row.cambios.map((c, ci) => {
                                const sube = parseFloat(String(c.a)) > parseFloat(String(c.de));
                                const baja = parseFloat(String(c.a)) < parseFloat(String(c.de));
                                return (
                                  <span key={ci} style={{ display: 'block', color: sube ? '#2ecc71' : baja ? '#ff6b6b' : style.color, fontWeight: 700 }}>
                                    {c.campo}: {c.de} → {c.a}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  }
                </>
              );
            })()}
          </div>
        </Popup>,
        document.body
      )}
      {showNotes && (
        <Popup type="centered" onClose={() => setShowNotes(false)} zIndex={10002}>
          <div style={{ padding: '16px 12px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: style.color, margin: 0, fontSize: 18, fontWeight: 800 }}>Notas de Entrenamiento</h3>
              <button onClick={() => setShowNotes(false)} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: '4px' }}>✕</button>
            </div>
            <textarea
              value={notes}
              onChange={e => { const v = e.target.value; setNotes(v); saveExLS(ex.name, { notes: v }); onUpdateField('notes', v); }}
              placeholder="Apunta pesos, sensaciones, posturas..."
              rows={4}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: `1px solid ${style.color}30`, borderRadius: 14, padding: '14px 16px', color: COLORS.text, fontSize: 15, outline: 'none', boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit', marginBottom: 16 }}
            />
            {/* Fotos de máquina */}
            <div>
              <label style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 1, display: 'block', marginBottom: 8, textTransform: 'uppercase', fontWeight: 700 }}>Fotos y Ajustes</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {exPhotos.map(foto => (
                  <div key={foto.id} style={{ position: 'relative', width: 80, height: 80, borderRadius: 12, overflow: 'hidden', border: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
                    <img src={foto.url} alt="" onClick={() => setLightboxUrl(foto.url)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }} />
                    <button
                      onClick={() => handleDeletePhoto(foto)}
                      style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.8)', border: 'none', color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}
                    >✕</button>
                  </div>
                ))}
                {exPhotos.length < 3 && (
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    style={{ width: 80, height: 80, borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: `1px dashed ${style.color}60`, color: style.color, fontSize: uploadingPhoto ? 12 : 24, cursor: uploadingPhoto ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700 }}
                  >{uploadingPhoto ? '...' : '📷'}</button>
                )}
                <input ref={photoInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} style={{ display: 'none' }} />
              </div>
            </div>
          </div>
        </Popup>
      )}

      {/* Campos: Reps + Peso (compartidos por todas las series) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div>
          <label style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Repeticiones</label>
          <div style={{ width: '100%', boxSizing: 'border-box', aspectRatio: '1.618', display: 'flex', alignItems: 'stretch', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <button
              onClick={() => onUpdateField('reps', String(Math.max(0, (parseInt(ex.reps) || 0) - 1)))}
              style={{ width: 36, flexShrink: 0, background: 'none', border: 'none', borderRight: `1px solid ${COLORS.border}`, color: '#fff', fontSize: 20, fontWeight: 700, cursor: 'pointer' }}
            >−</button>
            <input
              type="text"
              inputMode="numeric"
              value={ex.reps}
              onChange={e => onUpdateField('reps', e.target.value)}
              placeholder="—"
              style={{ width: 0, flex: 1, minWidth: 0, background: 'none', border: 'none', padding: '0 4px', color: style.color, fontSize: 26, fontWeight: 800, textAlign: 'center', outline: 'none' }}
            />
            <button
              onClick={() => { if (!bloqueoEx) onUpdateField('reps', String((parseInt(ex.reps) || 0) + 1)); }}
              style={{ width: 36, flexShrink: 0, background: 'none', border: 'none', borderLeft: `1px solid ${COLORS.border}`, color: bloqueoEx ? COLORS.textMuted : '#fff', fontSize: bloqueoEx ? 14 : 20, fontWeight: 700, cursor: bloqueoEx ? 'default' : 'pointer', opacity: bloqueoEx ? 0.6 : 1 }}
            >{bloqueoEx ? '🔒' : '+'}</button>
          </div>
        </div>
        <div>
          <label style={{ color: esPesoCorporal ? style.color : COLORS.textDim, fontSize: 11, letterSpacing: 0.5, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>{esPesoCorporal ? 'Lastre (kg)' : 'Peso (kg)'}{pesoRed !== null ? ` · −${pct}%` : ''}</label>
          <div style={{ width: '100%', boxSizing: 'border-box', aspectRatio: '1.618', display: 'flex', alignItems: 'stretch', position: 'relative', background: ex.subir_peso ? '#2ecc7132' : COLORS.bg, border: `1px solid ${pesoRed !== null ? '#2ecc7170' : (ex.subir_peso ? '#2ecc7170' : (esPesoCorporal ? style.color + '50' : COLORS.border))}`, borderRadius: 10, overflow: 'hidden', transition: 'background 0.2s, border-color 0.2s' }}>
            {/* Punto indicador — listener nativo passive:false para bloquear foco del input en móvil */}
            <div
              ref={subirPesoRef}
              onTouchEnd={e => { e.preventDefault(); e.stopPropagation(); if (!bloqueoEx || ex.subir_peso) onUpdateField('subir_peso', ex.subir_peso ? '' : 'true'); }}
              onClick={e => { e.stopPropagation(); if (!bloqueoEx || ex.subir_peso) onUpdateField('subir_peso', ex.subir_peso ? '' : 'true'); }}
              style={{
                position: 'absolute', top: 7, left: '50%', transform: 'translateX(-50%)',
                width: 10, height: 10, borderRadius: '50%',
                background: ex.subir_peso ? '#2ecc71' : (bloqueoEx ? COLORS.border : '#2ecc7166'),
                cursor: bloqueoEx && !ex.subir_peso ? 'default' : 'pointer', zIndex: 2,
                transition: 'background 0.2s',
                userSelect: 'none', WebkitUserSelect: 'none',
                touchAction: 'none',
              }}
            />
            <button
              onClick={() => onUpdateField('weight', String(Math.max(0, (parseFloat(ex.weight) || 0) - 2.5)))}
              style={{ width: 36, flexShrink: 0, background: 'none', border: 'none', borderRight: `1px solid ${esPesoCorporal ? style.color + '30' : COLORS.border}`, color: '#fff', fontSize: 20, fontWeight: 700, cursor: 'pointer' }}
            >−</button>
            {pesoRed !== null ? (
              // Descarga activa: peso de rutina tachado + peso reducido (el que toca levantar)
              <div style={{ width: 0, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1 }}>
                <s style={{ color: style.color, fontSize: 14, fontWeight: 700, opacity: 0.4 }}>{ex.weight}</s>
                <span style={{ color: '#2ecc71', fontSize: 24, fontWeight: 800 }}>{pesoRed}</span>
              </div>
            ) : (
              <input
                type="text"
                inputMode="decimal"
                value={ex.weight}
                onChange={e => onUpdateField('weight', e.target.value)}
                placeholder={esPesoCorporal ? '0' : '—'}
                style={{ width: 0, flex: 1, minWidth: 0, background: 'none', border: 'none', padding: '0 4px', color: style.color, fontSize: 26, fontWeight: 800, textAlign: 'center', outline: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
              />
            )}
            <button
              onClick={() => { if (bloqueoEx) return; onUpdateField('weight', String((parseFloat(ex.weight) || 0) + 2.5)); if (ex.subir_peso) onUpdateField('subir_peso', ''); }}
              style={{ width: 36, flexShrink: 0, background: 'none', border: 'none', borderLeft: `1px solid ${esPesoCorporal ? style.color + '30' : COLORS.border}`, color: bloqueoEx ? COLORS.textMuted : '#fff', fontSize: bloqueoEx ? 14 : 20, fontWeight: 700, cursor: bloqueoEx ? 'default' : 'pointer', opacity: bloqueoEx ? 0.6 : 1 }}
            >{bloqueoEx ? '🔒' : '+'}</button>
          </div>
        </div>
      </div>

      {/* Descarga + peso corporal: excluido de la reducción, ofrecer alternativa en máquina */}
      {descarga && esPesoCorporal && (
        <div style={{ marginTop: -8, marginBottom: 18, background: '#2ecc7110', border: '1px solid #2ecc7135', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ color: COLORS.textDim, fontSize: 11, lineHeight: 1.5, marginBottom: 8 }}>
            🪶 Descarga: los ejercicios de peso corporal no se reducen (quitar lastre apenas baja la carga real).
          </div>
          {onBuscarAlternativa && (
            <button
              onClick={onBuscarAlternativa}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: '#2ecc7118', border: '1px solid #2ecc7145', color: '#2ecc71', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}
            >💬 Buscar alternativa en máquina con FitCoach</button>
          )}
        </div>
      )}

      {/* Burbujas de series */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, marginBottom: 8, textTransform: 'uppercase' }}>
          Series — {doneCount}/{ex.sets.length} completadas
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {ex.sets.map((set, si) => (
            <div
              key={set.id}
              onClick={() => {
                if (deleteMode === si) return;
                setDeleteMode(null);
                if (set.done) onToggleSet(si);
              }}
              onTouchStart={() => {
                setLongRef.current = setTimeout(() => setDeleteMode(si), 500);
              }}
              onTouchEnd={() => clearTimeout(setLongRef.current)}
              onTouchCancel={() => clearTimeout(setLongRef.current)}
              style={{
                width: 48, height: 48,
                borderRadius: 14,
                background: deleteMode === si ? '#ff2d5520' : set.done ? COLORS.green : COLORS.bg,
                border: `2px solid ${deleteMode === si ? '#ff2d55' : set.done ? COLORS.green : COLORS.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: set.done ? `0 4px 12px ${COLORS.green}40` : 'none',
                flexShrink: 0, position: 'relative',
              }}
            >
              {deleteMode === si
                ? <span onClick={(e) => { e.stopPropagation(); onDeleteSet(si); setDeleteMode(null); }} style={{ color: '#ff2d55', fontSize: 20 }}>🗑</span>
                : set.done
                  ? <span style={{ color: '#fff', fontSize: 18, fontWeight: 800 }}>✓</span>
                  : <span style={{ color: COLORS.textMuted, fontSize: 18, fontWeight: 800 }}>{si + 1}</span>
              }
            </div>
          ))}
          <div
            onClick={() => { if (!setsAlLimite) onAddSet(); }}
            style={{
              width: 48, height: 48, borderRadius: 14,
              background: COLORS.bg,
              border: `2px dashed ${COLORS.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: setsAlLimite ? 'default' : 'pointer', flexShrink: 0, marginLeft: 'auto',
              opacity: setsAlLimite ? 0.45 : 1,
            }}
          >
            <span style={{ color: COLORS.textMuted, fontSize: setsAlLimite ? 16 : 22, fontWeight: 700, lineHeight: 1 }}>{setsAlLimite ? '🔒' : '+'}</span>
          </div>
        </div>
      </div>

      {/* Temporizador */}
      <div style={{ marginBottom: 20, userSelect: 'none', WebkitUserSelect: 'none' }}>
        <label style={{ color: COLORS.textDim, fontSize: 11, letterSpacing: 0.5, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>⏱ Tiempo</label>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
          {/* Descanso prefijado con +/- */}
          <div style={{ display: 'flex', alignItems: 'stretch', background: COLORS.bg, border: '1px solid ' + COLORS.border, borderRadius: 12, overflow: 'hidden', flexShrink: 0 }}>
            <button
              onClick={(e) => { e.stopPropagation(); applyRestChange(-15); }}
              style={{ width: 32, background: 'none', border: 'none', borderRight: '1px solid ' + COLORS.border, color: COLORS.text, fontSize: 18, fontWeight: 700, cursor: 'pointer' }}
            >−</button>
            <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 56 }}>
              <span style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.5, marginBottom: 2, textTransform: 'uppercase' }}>descanso</span>
              <span style={{ color: COLORS.text, fontSize: 17, fontWeight: 800, fontFamily: 'monospace' }}>{restLabel}</span>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); applyRestChange(15); }}
              style={{ width: 32, background: 'none', border: 'none', borderLeft: '1px solid ' + COLORS.border, color: COLORS.text, fontSize: 18, fontWeight: 700, cursor: 'pointer' }}
            >+</button>
          </div>
          {/* Contador progresivo (grande) */}
          <div onClick={handleCounterTap} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: phase === 'resting' ? (isOverRest ? '#ff2d5512' : '#2ecc7112') : phase === 'active' ? style.color + '12' : COLORS.bg, border: '1px solid ' + (phase === 'resting' ? (isOverRest ? '#ff2d5540' : '#2ecc7140') : phase === 'active' ? style.color + '40' : COLORS.border), borderRadius: 12, padding: '10px 8px', cursor: phase !== 'ready' ? 'pointer' : 'default', userSelect: 'none' }}>
            <span style={{ color: counterColor, fontSize: 38, fontWeight: 900, fontFamily: 'monospace', opacity: counterBlink ? 0.2 : paused ? 0.45 : 1, transition: 'color 0.3s' }}>{timerLabel}</span>
            {paused ? (
              <div style={{ marginTop: 3, color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>en pausa</div>
            ) : phase === 'resting' ? (
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ color: '#85967c', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>ejercicio</span>
                <span style={{ color: style.color, fontSize: 13, fontWeight: 800, fontFamily: 'monospace' }}>
                  {String(Math.floor(exerciseEndElapsed / 60)).padStart(2,'0')}:{String(exerciseEndElapsed % 60).padStart(2,'0')}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Acciones — C1: INICIAR / SIGUIENTE / COMPLETADO */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={onNextClick} style={{ flex: 1, padding: 15, borderRadius: 14, background: `linear-gradient(135deg, ${style.color}, ${style.color}cc)`, border: 'none', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>{nextBtnLabel}</button>
      </div>

      {/* Popup editar intervalo */}
      {lightboxUrl && (
        <div onClick={() => setLightboxUrl(null)} style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <img src={lightboxUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12, objectFit: 'contain' }} />
          <button onClick={() => setLightboxUrl(null)} style={{ position: 'absolute', top: 16, right: 16, width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
      )}
      {showTimerEdit && (
        <Popup type="centered" onClose={() => setShowTimerEdit(false)} zIndex={10000}>
          <div style={{ textAlign: 'center', userSelect: 'none', WebkitUserSelect: 'none' }}>
            <p style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 1, margin: '0 0 20px', textTransform: 'uppercase' }}>Tiempo de descanso</p>
            <button
              onClick={() => setRestDuration(d => d + 30)}
              style={{ width: '100%', padding: '12px 0', borderRadius: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.text, fontSize: 24, cursor: 'pointer', marginBottom: 12 }}
            >▲</button>
            <div style={{ color: style.color, fontSize: 36, fontWeight: 900, fontFamily: 'monospace', marginBottom: 12 }}>
              {Math.floor(restDuration / 60)}:{String(restDuration % 60).padStart(2, '0')}
            </div>
            <button
              onClick={() => setRestDuration(d => Math.max(30, d - 30))}
              style={{ width: '100%', padding: '12px 0', borderRadius: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.text, fontSize: 24, cursor: 'pointer', marginBottom: 20 }}
            >▼</button>
            <button onClick={() => {
              localStorage.setItem(storageKey, String(restDuration));
              onUpdateRest(restDuration);
              setShowTimerEdit(false);
            }} style={{ width: '100%', padding: 14, borderRadius: 12, background: `linear-gradient(135deg, ${style.color}, ${style.color}cc)`, border: 'none', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 15 }}>LISTO</button>
          </div>
        </Popup>
      )}
    </div>
  );
}
